import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { buildRequestVoucherHtml, buildLiquidationDocumentHtml } from "../../utils/costAnalysisBasisHtml";
import { buildCheckVoucherPrintHtml } from "../../utils/checkVoucherHtml";
import { buildCostAnalysisReportHtml } from "../../utils/costAnalysisReportHtml";
import { getCostAnalysisPayeeLabel, getCostAnalysisPayeeValue } from "../../utils/costAnalysisPayee";
import { API_BASE_URL, authFetch } from "../../config/api";
import supabase from "../../supabaseClient";

const money = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

const fileIconClass = (fileName) => {
  const ext = String(fileName || "").split(".").pop()?.toLowerCase() || "";
  if (ext === "pdf") return "fa-file-pdf";
  if (["png", "jpg", "jpeg", "webp", "gif"].includes(ext)) return "fa-file-image";
  if (["doc", "docx"].includes(ext)) return "fa-file-word";
  if (["xls", "xlsx", "csv"].includes(ext)) return "fa-file-excel";
  return "fa-file";
};

const fileIconColor = (fileName) => {
  const ext = String(fileName || "").split(".").pop()?.toLowerCase() || "";
  if (ext === "pdf") return "text-red-500";
  if (["png", "jpg", "jpeg", "webp", "gif"].includes(ext)) return "text-blue-500";
  if (["doc", "docx"].includes(ext)) return "text-blue-600";
  if (["xls", "xlsx", "csv"].includes(ext)) return "text-green-600";
  return "text-slate-500";
};

const readJsonResponse = async (res) => {
  const raw = await res.text();
  try {
    return JSON.parse(raw);
  } catch {
    const preview = raw.trim().slice(0, 120);
    throw new Error(
      preview.startsWith("<!DOCTYPE") || preview.startsWith("<html")
        ? "API returned HTML instead of JSON. Check the backend URL or auth session."
        : "API returned an unexpected non-JSON response."
    );
  }
};

const stageMeta = {
  pending_submission: {
    label: "Awaiting entry",
    tone: "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
    description: "Enter billing and receipted charges.",
  },
  draft: {
    label: "Draft",
    tone: "bg-orange-50 text-orange-700 ring-1 ring-orange-200",
    description: "Saved but not yet submitted for approval.",
  },
  submitted: {
    label: "Submitted",
    tone: "bg-sky-50 text-sky-700 ring-1 ring-sky-200",
    description: "Cost analysis submitted for verification.",
  },
  verified: {
    label: "Partially Approved",
    tone: "bg-violet-50 text-violet-700 ring-1 ring-violet-200",
    description: "Approved by User 130; awaiting final approval by User 127.",
  },
  partially_approved: {
    label: "Partially Approved",
    tone: "bg-violet-50 text-violet-700 ring-1 ring-violet-200",
    description: "Approved by User 130; awaiting final approval by User 127.",
  },
  approved: {
    label: "Approved",
    tone: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
    description: "Cost analysis approved by VP Operations.",
  },
  rejected: {
    label: "Rejected",
    tone: "bg-rose-50 text-rose-700 ring-1 ring-rose-200",
    description: "Cost analysis rejected and needs correction.",
  },
  completed: {
    label: "Completed",
    tone: "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
    description: "Cost analysis process completed.",
  },
  waiting_request_approval: {
    label: "Waiting approval",
    tone: "bg-neutral-100 text-neutral-700 ring-1 ring-neutral-200",
    description: "The request must be approved first.",
  },
};

const stageOrder = [
  "pending_submission",
  "draft",
  "submitted",
  "partially_approved",
  "approved",
  "completed",
];

// Shared with the clickable stat cards, so the card counts and the
// "click to filter the table" behavior always agree on the same buckets.
const stageFilterGroups = {
  awaiting: ["pending_submission"],
  inProgress: ["draft", "submitted", "partially_approved", "verified"],
  approved: ["approved"],
  completed: ["completed"],
};

const stageFilterLabels = {
  awaiting: "Awaiting Entry",
  inProgress: "In Progress",
  approved: "Approved",
  completed: "Completed",
};

const actionTimeline = (costAnalysis, report) => [
  { label: "Submitted", value: costAnalysis?.submitted_at, actor: costAnalysis?.submitted_by_name },
  { label: "Partially Approved", value: costAnalysis?.verified_at, actor: costAnalysis?.verified_by_name },
  { label: "Approved", value: costAnalysis?.approved_at, actor: costAnalysis?.approved_by_name },
  { label: "Rejected", value: costAnalysis?.rejected_at, actor: costAnalysis?.rejected_by_name },
].filter((item) => item.value || item.actor);

const normalizeJobOrder = (value) => String(value || "").trim().toUpperCase();

// Ranks a request's progress so the row representing the furthest-along cost
// analysis wins the group, regardless of which sibling request was touched
// most recently (pending_submission siblings with a later updated_at should
// never mask an in-progress/approved cost analysis on the same job order).
const stageProgressRank = (stage) => {
  const index = stageOrder.indexOf(stage === "verified" ? "partially_approved" : stage);
  return index === -1 ? 0 : index;
};

const groupRequestsByJobOrder = (rows = []) => {
  const grouped = new Map();

  rows.forEach((row) => {
    const jobOrderKey = normalizeJobOrder(row?.job_order) || `__request__:${row?.id || Math.random()}`;
    const existing = grouped.get(jobOrderKey);
    const currentTotalPhp = Number(row?.total_amount || 0);
    // Calculate USD amount for this request (if USD, divide by exchange rate)
    const rowCurrency = String(row?.currency || "PHP").toUpperCase();
    const rowExchangeRate = Number(row?.exchange_rate || 1);
    const currentTotalUsd = rowCurrency === "USD" && rowExchangeRate > 0
      ? currentTotalPhp / rowExchangeRate
      : 0;

    if (!existing) {
      grouped.set(jobOrderKey, {
        ...row,
        jo_request_count: 1,
        jo_total_amount: currentTotalPhp,
        jo_total_amount_usd: currentTotalUsd,
        jo_request_ids: row?.id ? [row.id] : [],
        jo_requests: [row],
      });
      return;
    }

    const existingRank = stageProgressRank(existing.cost_analysis_stage);
    const incomingRank = stageProgressRank(row.cost_analysis_stage);
    let representative;
    if (incomingRank !== existingRank) {
      representative = incomingRank > existingRank ? row : existing;
    } else {
      const existingUpdatedAt = new Date(existing.updated_at || existing.created_at || 0).getTime();
      const incomingUpdatedAt = new Date(row.updated_at || row.created_at || 0).getTime();
      representative = incomingUpdatedAt >= existingUpdatedAt ? row : existing;
    }

    grouped.set(jobOrderKey, {
      ...representative,
      jo_request_count: existing.jo_request_count + 1,
      jo_total_amount: Number(existing.jo_total_amount || 0) + currentTotalPhp,
      jo_total_amount_usd: Number(existing.jo_total_amount_usd || 0) + currentTotalUsd,
      jo_request_ids: [...existing.jo_request_ids, row?.id].filter(Boolean),
      jo_requests: [...(existing.jo_requests || []), row],
    });
  });

  return Array.from(grouped.values());
};

const createBillingChargeRow = (row = {}, defaults = {}) => ({
  id: row.id || `${Date.now()}-${Math.random()}`,
  particulars: row.particulars || "",
  currency: String(row.currency || defaults.currency || "PHP").toUpperCase(),
  exchange_rate: row.exchange_rate ?? defaults.exchange_rate ?? 1,
  has_output_vat: row.has_output_vat ?? false,
  has_ewt: row.has_ewt ?? false,
  ewt_rate: row.ewt_rate || "",
  base_amount: row.base_amount ?? "",
  computed_amount: row.computed_amount ?? "",
});

const normalizeChargeParticulars = (row = {}) =>
  row.particulars || row.cost_incurred || row.description || row.name || "";

const createReceiptedChargeRow = (row = {}) => ({
  id: row.id || `${Date.now()}-${Math.random()}`,
  particulars: normalizeChargeParticulars(row),
  or_no: row.or_no || "",
  amount: row.amount ?? "",
  source_type: row.source_type || "",
  source_request_id: row.source_request_id || row.request_id || "",
  source_particular_id: row.source_particular_id || row.particular_id || "",
  source_rfp_no: row.source_rfp_no || row.rfp_no || "",
  source_payee: row.source_payee || row.payee || "",
  liquidation_no: row.liquidation_no || row.source_liquidation_no || "",
  liquidation_id: row.liquidation_id || row.source_liquidation_id || "",
  liquidation_status: row.liquidation_status || row.source_liquidation_status || "",
});

const createReceiptedChargeRowFromRequest = (row = {}) => ({
  id: `${row.source_request_id || row.request_id || "request"}-${row.source_particular_id || row.id || Math.random()}`,
  particulars: normalizeChargeParticulars(row),
  or_no: row.or_no || "",
  amount: row.amount ?? row.total ?? "",
  source_type: "request_particular",
  source_request_id: row.source_request_id || row.request_id || "",
  source_particular_id: row.source_particular_id || "",
  source_rfp_no: row.source_rfp_no || row.rfp_no || "",
  source_payee: row.source_payee || row.payee || "",
  liquidation_no: "",
  liquidation_id: "",
  liquidation_status: "",
});

const createReceiptedChargeRowFromLiquidation = (row = {}) => ({
  id: `${row.liquidation_id || row.liquidation_no || "liquidation"}-${row.id || Math.random()}`,
  particulars: normalizeChargeParticulars(row),
  or_no: row.or_no || "",
  amount: row.amount ?? "",
  source_type: "liquidation_actual_cost",
  source_request_id: row.source_request_id || row.request_id || "",
  source_particular_id: "",
  source_rfp_no: row.source_rfp_no || row.rfp_no || "",
  liquidation_no: row.liquidation_no || "",
  liquidation_id: row.liquidation_id || "",
  liquidation_status: row.liquidation_status || "",
});

const createActualCostRow = (row = {}) => ({
  id: row.id || `${Date.now()}-${Math.random()}`,
  particulars: normalizeChargeParticulars(row),
  or_no: row.or_no || "",
  amount: row.amount ?? "",
  source_type: row.source_type || "",
  source_request_id: row.source_request_id || row.request_id || "",
  source_particular_id: row.source_particular_id || row.particular_id || "",
  source_rfp_no: row.source_rfp_no || row.rfp_no || "",
  source_payee: row.source_payee || row.payee || "",
  liquidation_no: row.liquidation_no || row.source_liquidation_no || "",
  liquidation_id: row.liquidation_id || row.source_liquidation_id || "",
  liquidation_status: row.liquidation_status || row.source_liquidation_status || "",
});

const hasMeaningfulChargeRow = (row = {}) =>
  Boolean(
    String(row.particulars || "").trim() ||
      String(row.or_no || "").trim() ||
      String(row.amount || "").trim()
  );

const hasMeaningfulActualCostRow = hasMeaningfulChargeRow;

const buildReceiptedSourceKey = (row = {}) =>
  [
    String(row?.source_type || "").trim(),
    String(row?.source_request_id || "").trim(),
    String(row?.source_particular_id || "").trim(),
    String(row?.source_rfp_no || "").trim(),
    String(row?.liquidation_id || row?.source_liquidation_id || "").trim(),
    normalizeChargeParticulars(row),
    String(row?.or_no || "").trim(),
    Number(row?.amount || 0).toFixed(2),
  ].join("|");

const buildActualCostSourceKey = (row = {}) =>
  [
    String(row?.source_type || "").trim(),
    String(row?.source_request_id || "").trim(),
    String(row?.source_particular_id || "").trim(),
    String(row?.source_rfp_no || "").trim(),
    String(row?.liquidation_id || row?.source_liquidation_id || "").trim(),
    normalizeChargeParticulars(row),
    String(row?.or_no || "").trim(),
    Number(row?.amount || 0).toFixed(2),
  ].join("|");

const isFixedChargeParticular = (particular = {}) => Boolean(particular?.is_fixed_charge);

const createActualCostRowFromFixedCharges = (particular = {}) => ({
  id:
    particular.id && (particular.source_request_id || particular.request_id)
      ? `${particular.source_request_id || particular.request_id}-fixed-${particular.id}`
      : particular.id || `${Date.now()}-${Math.random()}`,
  particulars: normalizeChargeParticulars(particular),
  or_no: "",
  amount: particular.total ?? particular.amount ?? 0,
  source_type: "fixed_charge",
  source_request_id: particular.source_request_id || particular.request_id || "",
  source_particular_id: particular.id || "",
  source_rfp_no: particular.source_rfp_no || particular.rfp_no || "",
  source_payee: particular.source_payee || particular.payee || "",
  liquidation_no: particular.liquidation_no || particular.source_liquidation_no || "",
  liquidation_id: particular.liquidation_id || particular.source_liquidation_id || "",
  liquidation_status: particular.liquidation_status || particular.source_liquidation_status || "",
});

const createActualCostRowFromRequest = (row = {}) => ({
  id: `${row.source_request_id || row.request_id || "request"}-actual-${row.source_particular_id || row.id || Math.random()}`,
  particulars: normalizeChargeParticulars(row),
  or_no: row.or_no || "",
  amount: row.amount ?? row.total ?? "",
  source_type: "request_particular",
  source_request_id: row.source_request_id || row.request_id || "",
  source_particular_id: row.source_particular_id || "",
  source_rfp_no: row.source_rfp_no || row.rfp_no || "",
  source_payee: row.source_payee || row.payee || "",
  liquidation_no: "",
  liquidation_id: "",
  liquidation_status: "",
});

const createActualCostRowFromLiquidation = (row = {}) => ({
  id: `${row.liquidation_id || row.liquidation_no || "liquidation"}-actual-${row.id || Math.random()}`,
  particulars: normalizeChargeParticulars(row),
  or_no: row.or_no || "",
  amount: row.amount ?? "",
  source_type: "liquidation_actual_cost",
  source_request_id: row.source_request_id || row.request_id || "",
  source_particular_id: "",
  source_rfp_no: row.source_rfp_no || row.rfp_no || "",
  source_payee: row.source_payee || row.payee || "",
  liquidation_no: row.liquidation_no || "",
  liquidation_id: row.liquidation_id || "",
  liquidation_status: row.liquidation_status || "",
});

const CostAnalysisWorkflow = () => {
  const user = useSelector((state) => state.auth.user);
  const [scopeFilter, setScopeFilter] = useState("created");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [reportCurrentPage, setReportCurrentPage] = useState(1);
  const [reportPageSize, setReportPageSize] = useState(10);
  const [activeId, setActiveId] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [successModal, setSuccessModal] = useState(null);
  const [shipmentDetails, setShipmentDetails] = useState(null);
  const [shipmentDocuments, setShipmentDocuments] = useState([]);
  const [shipmentDocumentsLoading, setShipmentDocumentsLoading] = useState(false);
  const [reportDateCreated, setReportDateCreated] = useState(() => new Date().toISOString().slice(0, 10));
  const [reportDock, setReportDock] = useState("");
  const [reportPoNo, setReportPoNo] = useState("");
  const [reportReferenceNo, setReportReferenceNo] = useState("");
  const [reportDeliveryDate, setReportDeliveryDate] = useState("");
  const [reportPayee, setReportPayee] = useState("");
  const [reportBillTo, setReportBillTo] = useState("");
  const [billToSameAsConsignee, setBillToSameAsConsignee] = useState(true);
  const [billingChargeRows, setBillingChargeRows] = useState([createBillingChargeRow()]);
  const [receiptedChargeRows, setReceiptedChargeRows] = useState([createReceiptedChargeRow()]);
  const [actualCostRows, setActualCostRows] = useState([createActualCostRow()]);
  const [reportLoading, setReportLoading] = useState(false);
  const [downloadLoadingId, setDownloadLoadingId] = useState("");
  const [modalMode, setModalMode] = useState("editor");
  const [particularsOptions, setParticularsOptions] = useState([]);
  const [teamLeaderUsers, setTeamLeaderUsers] = useState([]);
  const [selectedTeamLeaderId, setSelectedTeamLeaderId] = useState("");
  const [focusReportSection, setFocusReportSection] = useState(false);
  const [activeJobOrder, setActiveJobOrder] = useState("");
  const [joLedgerDetail, setJoLedgerDetail] = useState(null);
  const [joLedgerLoading, setJoLedgerLoading] = useState(false);
  const [joLedgerError, setJoLedgerError] = useState("");
  const [liquidationActualCostRows, setLiquidationActualCostRows] = useState([]);
  const [previewDocType, setPreviewDocType] = useState("requests");
  const [previewSelection, setPreviewSelection] = useState({
    requests: 0,
    liquidations: 0,
    vouchers: 0,
    cost: 0,
  });
  const [voucherLinesById, setVoucherLinesById] = useState({});
  const [selectedAttachmentKeys, setSelectedAttachmentKeys] = useState(() => new Set());
  const [savingAttachments, setSavingAttachments] = useState(false);
  const [previewIframeHeight, setPreviewIframeHeight] = useState(620);
  const [previewZoom, setPreviewZoom] = useState(1);
  const reportSectionRef = useRef(null);
  const referenceDataLoadedRef = useRef(false);
  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [attachmentModalData, setAttachmentModalData] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const attachmentInputRef = useRef(null);
  const [notificationModal, setNotificationModal] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateClients, setTemplateClients] = useState([]);
  const [templateClient, setTemplateClient] = useState("");
  const [templateBillingRows, setTemplateBillingRows] = useState([createBillingChargeRow()]);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);

  const resolveUserName = (userRecord) =>
    [userRecord?.first_name, userRecord?.last_name].filter(Boolean).join(" ") ||
    userRecord?.user_name ||
    userRecord?.name ||
    "";

  const loadRequests = async (scope = scopeFilter) => {
    setLoading(true);
    setError("");
    try {
      const url = new URL(`${API_BASE_URL}/api/accounting/cost-analysis`);
      url.searchParams.set("summary", "true");
      if (scope === "all") url.searchParams.set("scope", "all");
      let lastError;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const res = await authFetch(url, { credentials: "include" });
          const json = await readJsonResponse(res);
          if (!res.ok || !json.success) {
            throw new Error(json.message || `Failed to load cost analysis requests (${res.status})`);
          }
          setRequests(json.data || []);
          return;
        } catch (err) {
          lastError = err;
          if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 350 * (attempt + 1)));
        }
      }
      throw lastError || new Error("Failed to load cost analysis requests");
    } catch (err) {
      setError(err.message || "Failed to load cost analysis requests");
    } finally {
      setLoading(false);
    }
  };

  const loadParticularsOptions = async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/particulars`, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (json.success) {
        const activeParticulars = (json.data || []).filter((p) => p.status === "active");
        setParticularsOptions(activeParticulars);
      }
    } catch (err) {
      console.error("Failed to load particulars options", err);
    }
  };

  const loadTeamLeaderUsers = async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/team-leader-users`, {
        credentials: "include",
      });
      const json = await readJsonResponse(res);
      if (json.success) {
        setTeamLeaderUsers(json.data || []);
      }
    } catch (err) {
      console.error("Failed to load team leader users", err);
    }
  };

  const openTemplateModal = async () => {
    setTemplateModalOpen(true);
    setTemplateLoading(true);
    try {
      const [clientsResponse, templatesResponse] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/templates/clients`, { credentials: "include" }),
        authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/templates`, { credentials: "include" }),
      ]);
      const clientsJson = await readJsonResponse(clientsResponse);
      const templatesJson = await readJsonResponse(templatesResponse);
      setTemplateClients(clientsJson.data || []);
      const firstClient = clientsJson.data?.[0] || "";
      setTemplateClient(firstClient);
      const existing = (templatesJson.data || []).find((item) => item.corporate_client === firstClient);
      setTemplateBillingRows(existing?.billing_charge_rows?.length ? existing.billing_charge_rows.map((row) => createBillingChargeRow(row, billingChargeDefaults)) : [createBillingChargeRow()]);
    } catch (err) {
      setTemplateModalOpen(false);
      showErrorModal(err.message || "Failed to load Cost Analysis templates");
    } finally {
      setTemplateLoading(false);
    }
  };

  const loadSelectedTemplate = async (client) => {
    setTemplateClient(client);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/templates`, { credentials: "include" });
      const json = await readJsonResponse(response);
      const existing = (json.data || []).find((item) => item.corporate_client === client);
      setTemplateBillingRows(existing?.billing_charge_rows?.length ? existing.billing_charge_rows.map((row) => createBillingChargeRow(row, billingChargeDefaults)) : [createBillingChargeRow()]);
    } catch (err) {
      showErrorModal(err.message || "Failed to load client template");
    }
  };

  const saveTemplate = async () => {
    if (!templateClient) return showErrorModal("Select a corporate client first.");
    setTemplateSaving(true);
    try {
      const response = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/templates`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ corporate_client: templateClient, billing_charge_rows: templateBillingRows }),
      });
      const json = await readJsonResponse(response);
      if (!json.success) throw new Error(json.message || "Failed to save template");
      setTemplateModalOpen(false);
      setSuccessModal({ title: "Template Saved", message: `Default billing charges for ${templateClient} have been saved.` });
    } catch (err) {
      showErrorModal(err.message || "Failed to save template");
    } finally {
      setTemplateSaving(false);
    }
  };

  const showErrorModal = useCallback((message) => {
    setNotificationModal({ type: "error", title: "Error", message });
  }, []);

  useEffect(() => {
    if (!isModalOpen || referenceDataLoadedRef.current) return;
    referenceDataLoadedRef.current = true;
    loadParticularsOptions();
    loadTeamLeaderUsers();
  }, [isModalOpen]);

  useEffect(() => {
    loadRequests(scopeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeFilter]);

  useEffect(() => {
    if (!activeId && requests[0]) {
      setActiveId(requests[0].id);
    }
  }, [activeId, requests]);

  useEffect(() => {
    if (!isModalOpen || !activeId) return;

    let cancelled = false;
    const loadDetail = async () => {
      setDetailLoading(true);
      setDetail(null);
      setLiquidationActualCostRows([]);
      try {
        console.debug("[CostAnalysis] loading detail", { activeId });
        const res = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/${activeId}`, {
          credentials: "include",
        });
        const json = await readJsonResponse(res);
        console.debug("[CostAnalysis] detail response", {
          success: json.success,
          hasLiquidationReport: Boolean(json.data?.liquidation_report),
          liquidationRowCount: json.data?.liquidation_report?.actual_cost_rows?.length || 0,
          costAnalysisStage: json.data?.cost_analysis_stage,
          hasCostAnalysisReport: Boolean(json.data?.cost_analysis_report),
        });
        if (!json.success) throw new Error(json.message || "Failed to load cost analysis details");
        if (!cancelled) {
          setDetail(json.data || null);
          if (json.data?.liquidation_report?.actual_cost_rows?.length) {
            console.debug("[CostAnalysis] seeding liquidation rows from detail payload", {
              count: json.data.liquidation_report.actual_cost_rows.length,
            });
            setLiquidationActualCostRows(json.data.liquidation_report.actual_cost_rows);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setDetail(null);
          setLiquidationActualCostRows([]);
          showErrorModal(err.message || "Failed to load cost analysis details");
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [activeId, isModalOpen, showErrorModal]);

  useEffect(() => {
    if (!isModalOpen) return undefined;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsModalOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen]);

  const searchFilteredRequests = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return requests;

    return requests.filter((row) => {
      const haystack = [
        row.rfp_no,
        row.payee,
        row.job_order,
        row.preparer_name,
        row.approver_name,
        row.cost_analysis_stage,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [requests, search]);

  // Counts always reflect the search term but ignore stageFilter itself, so
  // all six card totals stay visible no matter which card is active.
  const countableGroupedRequests = useMemo(
    () => groupRequestsByJobOrder(searchFilteredRequests),
    [searchFilteredRequests]
  );

  const groupedRequests = useMemo(() => {
    if (stageFilter === "all") return countableGroupedRequests;
    const allowedStages = stageFilterGroups[stageFilter] || [];
    return countableGroupedRequests.filter((row) => allowedStages.includes(row.cost_analysis_stage));
  }, [countableGroupedRequests, stageFilter]);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(groupedRequests.length / pageSize)),
    [groupedRequests.length, pageSize]
  );
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStart = groupedRequests.length ? (safeCurrentPage - 1) * pageSize + 1 : 0;
  const pageEnd = Math.min(safeCurrentPage * pageSize, groupedRequests.length);
  const paginatedGroupedRequests = useMemo(
    () => groupedRequests.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize),
    [groupedRequests, pageSize, safeCurrentPage]
  );
  const costAnalysisReportRows = useMemo(
    () =>
      groupedRequests
        .filter((row) => row.cost_analysis_report)
        .sort((a, b) => {
          const aDate = new Date(
            a.cost_analysis_report?.report_date_created ||
              a.cost_analysis_report?.updated_at ||
              a.updated_at ||
              0
          ).getTime();
          const bDate = new Date(
            b.cost_analysis_report?.report_date_created ||
              b.cost_analysis_report?.updated_at ||
              b.updated_at ||
              0
          ).getTime();
          return bDate - aDate;
        }),
    [groupedRequests]
  );
  const reportTotalPages = useMemo(
    () => Math.max(1, Math.ceil(costAnalysisReportRows.length / reportPageSize)),
    [costAnalysisReportRows.length, reportPageSize]
  );
  const safeReportCurrentPage = Math.min(reportCurrentPage, reportTotalPages);
  const reportPageStart = costAnalysisReportRows.length ? (safeReportCurrentPage - 1) * reportPageSize + 1 : 0;
  const reportPageEnd = Math.min(safeReportCurrentPage * reportPageSize, costAnalysisReportRows.length);
  const paginatedCostAnalysisReportRows = useMemo(
    () =>
      costAnalysisReportRows.slice(
        (safeReportCurrentPage - 1) * reportPageSize,
        safeReportCurrentPage * reportPageSize
      ),
    [costAnalysisReportRows, reportPageSize, safeReportCurrentPage]
  );

  const activeGroupRequests = useMemo(() => {
    const key = normalizeJobOrder(activeJobOrder);
    if (!key) return [];
    return requests.filter((row) => normalizeJobOrder(row?.job_order) === key);
  }, [activeJobOrder, requests]);

  const counts = useMemo(() => {
    const total = countableGroupedRequests.length;
    const awaiting = countableGroupedRequests.filter((row) => stageFilterGroups.awaiting.includes(row.cost_analysis_stage)).length;
    const inProgress = countableGroupedRequests.filter((row) => stageFilterGroups.inProgress.includes(row.cost_analysis_stage)).length;
    const partiallyApproved = countableGroupedRequests.filter((row) => ["partially_approved", "verified"].includes(row.cost_analysis_stage)).length;
    const approved = countableGroupedRequests.filter((row) => stageFilterGroups.approved.includes(row.cost_analysis_stage)).length;
    const completed = countableGroupedRequests.filter((row) => stageFilterGroups.completed.includes(row.cost_analysis_stage)).length;
    return { total, awaiting, inProgress, partiallyApproved, approved, completed };
  }, [countableGroupedRequests]);

  useEffect(() => {
    setCurrentPage(1);
    setReportCurrentPage(1);
  }, [search, scopeFilter, stageFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (reportCurrentPage > reportTotalPages) {
      setReportCurrentPage(reportTotalPages);
    }
  }, [reportCurrentPage, reportTotalPages]);

  const activeRequest = useMemo(
    () => {
      if (activeGroupRequests.length) {
        return (
          activeGroupRequests.find((row) => row.cost_analysis_report?.id || row.cost_analysis?.id) ||
          activeGroupRequests[0] ||
          null
        );
      }
      return requests.find((row) => String(row.id) === String(activeId)) || null;
    },
    [activeGroupRequests, activeId, requests]
  );

  const modalData = detail || activeRequest;
  const costAnalysis = modalData?.cost_analysis || null;
  const savedReport = modalData?.cost_analysis_report || null;
  const clientTemplateBillingRows = useMemo(
    () => modalData?.cost_analysis_template?.billing_charge_rows || [],
    [modalData?.cost_analysis_template?.billing_charge_rows]
  );
  const currentStage = modalData?.cost_analysis_stage || "pending_submission";
  const currentUserId = Number(user?.user_id || user?.id || 0);
  const isSuperadmin = String(user?.role || "").toLowerCase() === "superadmin";
  const selectedTeamLeaderUserId = Number(
    savedReport?.team_leader_user_id || costAnalysis?.team_leader_user_id || selectedTeamLeaderId || 0
  );
  const selectedTeamLeaderName =
    resolveUserName(
      teamLeaderUsers.find((leader) => String(leader.user_id) === String(selectedTeamLeaderUserId))
    ) ||
    savedReport?.team_leader_name ||
    costAnalysis?.team_leader_name ||
    "—";
  const initialApproverName =
    resolveUserName(teamLeaderUsers.find((approver) => String(approver.user_id) === "130")) ||
    savedReport?.initial_approver_name ||
    costAnalysis?.initial_approver_name ||
    "";
  const finalApproverName =
    resolveUserName(teamLeaderUsers.find((approver) => String(approver.user_id) === "127")) ||
    savedReport?.final_approver_name ||
    costAnalysis?.final_approver_name ||
    "";
  const normalizedCurrentStage = currentStage === "verified" ? "partially_approved" : currentStage;
  const currentMeta = stageMeta[normalizedCurrentStage] || stageMeta.pending_submission;
  const stageIndex = Math.max(stageOrder.indexOf(normalizedCurrentStage), 0);
  const progressWidth = `${Math.max(0, (stageIndex / (stageOrder.length - 1)) * 100)}%`;
  const billingChargeDefaults = useMemo(
    () => ({
      currency: String(modalData?.currency || "PHP").toUpperCase(),
      exchange_rate: Number(modalData?.exchange_rate || 1) || 1,
    }),
    [modalData?.currency, modalData?.exchange_rate]
  );

  const canSubmit =
    (currentStage === "pending_submission" || currentStage === "draft" || currentStage === "rejected") &&
    savedReport;
  const canVerify = currentStage === "submitted" && (isSuperadmin || currentUserId === 130);
  const canApprove = ["submitted", "partially_approved", "verified"].includes(currentStage) && (isSuperadmin || currentUserId === 127);
  const canReject = !["completed", "rejected", "pending_submission", "draft"].includes(currentStage);
  const creatorUserId = Number(
    modalData?.cost_analysis_report?.created_by_user_id ||
    modalData?.cost_analysis_report?.preparer_user_id ||
    modalData?.preparer_user_id ||
    0
  );
  const currentUserName = String(user?.user_name || "").trim().toLowerCase();
  const creatorName = String(
    modalData?.jo_creator_name ||
    modalData?.cost_analysis_report?.created_by_name ||
    modalData?.cost_analysis_report?.preparer_name ||
    modalData?.preparer_name ||
    ""
  ).trim().toLowerCase();
  const isCostAnalysisCreator =
    isSuperadmin ||
    (creatorUserId > 0 && creatorUserId === currentUserId) ||
    (Boolean(currentUserName && creatorName) && currentUserName === creatorName);
  const canEdit = isCostAnalysisCreator && currentStage !== "completed";

  const verifyDisabledReason =
    currentStage === "submitted" && !canVerify
      ? "Only User 130 can give initial approval."
      : "";

  // Load saved report data into form when modal opens
  useEffect(() => {
    if (!savedReport) {
      const defaultPayee =
        modalData?.jo_payee || getCostAnalysisPayeeValue(shipmentDetails, modalData?.payee || "") || "";
      // Reset to defaults
      setReportDateCreated(new Date().toISOString().slice(0, 10));
      setReportDock("");
      setReportPoNo("");
      setReportReferenceNo("");
      setReportDeliveryDate("");
      // Apply rule of payee: use backend-provided value or derive from shipment details
      setReportPayee(defaultPayee);
      setReportBillTo(defaultPayee);
      setBillToSameAsConsignee(true);
      setBillingChargeRows(clientTemplateBillingRows.length
        ? clientTemplateBillingRows.map((row) => createBillingChargeRow(row, billingChargeDefaults))
        : [createBillingChargeRow({}, billingChargeDefaults)]);
      setReceiptedChargeRows([createReceiptedChargeRow()]);
      setActualCostRows([createActualCostRow()]);
      setSelectedTeamLeaderId("");
      setSelectedAttachmentKeys(new Set());
      return;
    }

    const resolvedPayee =
      savedReport.consignee ||
      modalData?.jo_payee ||
      getCostAnalysisPayeeValue(shipmentDetails, modalData?.payee || "") ||
      "";
    const resolvedBillTo = savedReport.bill_to || "";
    const savedBillToSameAsConsignee =
      typeof savedReport.bill_to_same_as_consignee === "boolean"
        ? savedReport.bill_to_same_as_consignee
        : !resolvedBillTo || resolvedBillTo === resolvedPayee;

    setReportDateCreated(savedReport.report_date_created || new Date().toISOString().slice(0, 10));
    setReportDock(savedReport.dock || "");
    setReportPoNo(savedReport.po_no || "");
    setReportReferenceNo(savedReport.reference_no || "");
    setReportDeliveryDate(savedReport.delivery_date || "");
    // Load saved consignee, or apply rule of payee if not saved yet
    setReportPayee(resolvedPayee);
    setBillToSameAsConsignee(savedBillToSameAsConsignee);
    setReportBillTo(savedBillToSameAsConsignee ? resolvedPayee : resolvedBillTo);
    setSelectedTeamLeaderId(String(savedReport.team_leader_user_id || costAnalysis?.team_leader_user_id || ""));

    if (savedReport.billing_charge_rows?.length) {
      setBillingChargeRows(savedReport.billing_charge_rows.map((row) => createBillingChargeRow(row, billingChargeDefaults)));
    } else {
      setBillingChargeRows([createBillingChargeRow({}, billingChargeDefaults)]);
    }

    if (savedReport.receipted_charge_rows?.length) {
      const savedReceiptedRows = savedReport.receipted_charge_rows.map(createReceiptedChargeRow);
      setReceiptedChargeRows(savedReceiptedRows.some(hasMeaningfulChargeRow) ? savedReceiptedRows : [createReceiptedChargeRow()]);
    } else {
      setReceiptedChargeRows([createReceiptedChargeRow()]);
    }

    if (savedReport.actual_cost_rows?.length) {
      setActualCostRows(savedReport.actual_cost_rows.map(createActualCostRow));
    } else {
      setActualCostRows([createActualCostRow()]);
    }

    setSelectedAttachmentKeys(new Set((savedReport.attached_documents || []).map((doc) => doc.key).filter(Boolean)));
  }, [
    savedReport,
    costAnalysis,
    liquidationActualCostRows,
    billingChargeDefaults,
    modalData?.jo_payee,
    modalData?.payee,
    shipmentDetails,
    clientTemplateBillingRows,
  ]);

  useEffect(() => {
    if (!billToSameAsConsignee) return;
    setReportBillTo(reportPayee || "");
  }, [billToSameAsConsignee, reportPayee]);

  useEffect(() => {
    const fetchShipment = async () => {
      if (!isModalOpen) {
        setShipmentDetails(null);
        return;
      }

      const embeddedShipment = detail?.shipment_details || activeRequest?.shipment_details;
      if (embeddedShipment) {
        setShipmentDetails(embeddedShipment);
        return;
      }

      if (!activeRequest?.job_order) {
        setShipmentDetails(null);
        return;
      }

      try {
        const url = new URL(`${API_BASE_URL}/api/accounting/shipments`);
        url.searchParams.set("q", activeRequest.job_order);
        url.searchParams.set("limit", 1);
        const res = await authFetch(url, { credentials: "include" });
        const json = await readJsonResponse(res);
        if (json.success && json.data?.length) {
          setShipmentDetails(json.data[0]);
        } else {
          setShipmentDetails(null);
        }
      } catch (err) {
        console.error("shipment lookup", err);
        setShipmentDetails(null);
      }
    };

    fetchShipment();
  }, [activeRequest?.job_order, activeRequest?.shipment_details, detail?.shipment_details, isModalOpen]);

  useEffect(() => {
    const shipmentId = shipmentDetails?.shipment_id;
    if (!isModalOpen || !shipmentId) {
      setShipmentDocuments([]);
      return undefined;
    }

    let cancelled = false;

    const fetchShipmentDocuments = async () => {
      setShipmentDocumentsLoading(true);
      try {
        const { data, error } = await supabase
          .from("documents")
          .select("*")
          .eq("shipment_id", shipmentId)
          .order("uploaded_at", { ascending: false });
        if (error) throw error;
        if (!cancelled) setShipmentDocuments(data || []);
      } catch (err) {
        console.error("Failed to load shipment documents", err);
        if (!cancelled) setShipmentDocuments([]);
      } finally {
        if (!cancelled) setShipmentDocumentsLoading(false);
      }
    };

    fetchShipmentDocuments();
    return () => {
      cancelled = true;
    };
  }, [isModalOpen, shipmentDetails?.shipment_id]);

  useEffect(() => {
    if (!isModalOpen || !focusReportSection || !reportSectionRef.current) return undefined;

    const timer = window.setTimeout(() => {
      reportSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      setFocusReportSection(false);
    }, 150);

    return () => window.clearTimeout(timer);
  }, [activeId, focusReportSection, isModalOpen, savedReport]);

  useEffect(() => {
    if (!isModalOpen || !activeJobOrder) {
      setJoLedgerDetail(null);
      setJoLedgerError("");
      setJoLedgerLoading(false);
      setVoucherLinesById({});
      return undefined;
    }

    let cancelled = false;

    const loadJoLedgerDetail = async () => {
      setJoLedgerLoading(true);
      setJoLedgerError("");
      try {
        const res = await authFetch(
          `${API_BASE_URL}/api/accounting/jo-ledger/${encodeURIComponent(activeJobOrder)}`,
          { credentials: "include" }
        );
        const json = await readJsonResponse(res);
        if (!json.success) {
          throw new Error(json.message || "Failed to load JO ledger detail");
        }
        if (!cancelled) {
          setJoLedgerDetail(json.data || null);
        }
      } catch (err) {
        if (!cancelled) {
          setJoLedgerDetail(null);
          setJoLedgerError(err.message || "Failed to load JO ledger detail");
        }
      } finally {
        if (!cancelled) {
          setJoLedgerLoading(false);
        }
      }
    };

    loadJoLedgerDetail();
    return () => {
      cancelled = true;
    };
  }, [activeJobOrder, isModalOpen]);

  const refreshList = async (requestId) => {
    await loadRequests();
    if (requestId) {
      setActiveId(String(requestId));
    }
  };

  const resetModalFields = () => {
    setRejectReason("");
  };

  const showSuccessModal = (title, message) => {
    setSuccessModal({ title, message });
  };

  // Billing charge row handlers
  const addBillingChargeRow = () => {
    setBillingChargeRows((rows) => {
      const firstRow = rows[0] || {};
      const firstCurrency = String(firstRow.currency || billingChargeDefaults.currency || "PHP").toUpperCase();
      const firstExchangeRate =
        firstCurrency === "PHP"
          ? 1
          : Number(firstRow.exchange_rate || billingChargeDefaults.exchange_rate || 1) || 1;

      return [
        ...rows,
        createBillingChargeRow(
          {},
          {
            currency: firstCurrency,
            exchange_rate: firstExchangeRate,
          }
        ),
      ];
    });
  };

  const updateBillingChargeRow = (index, field, value) => {
    setBillingChargeRows((rows) =>
      rows.map((row, rowIndex) => {
        if (rowIndex !== index) return row;

        const nextRow = { ...row, [field]: value };
        const getTaxMultiplier = () => {
          const vatMultiplier = nextRow.has_output_vat ? 1.12 : 1;
          const ewtRate = nextRow.has_ewt ? Number.parseFloat(nextRow.ewt_rate || 0) : 0;
          return vatMultiplier * (1 + (Number.isFinite(ewtRate) ? ewtRate : 0) / 100);
        };
        const numberOrNull = (input) => {
          if (input === "" || input === null || input === undefined) return null;
          const number = Number(input);
          return Number.isFinite(number) && number > 0 ? number : null;
        };
        const autoComputeMissingField = () => {
          const amount = numberOrNull(nextRow.base_amount);
          const rate = numberOrNull(nextRow.exchange_rate);
          const computed = numberOrNull(nextRow.computed_amount);
          const multiplier = getTaxMultiplier();

          if (field === "exchange_rate" && rate && computed) {
            nextRow.base_amount = Number((computed / (rate * multiplier)).toFixed(2));
          } else if (field === "base_amount" && amount && rate) {
            nextRow.computed_amount = Number((amount * rate * multiplier).toFixed(2));
          } else if (field === "computed_amount" && amount && computed) {
            nextRow.exchange_rate = Number((computed / (amount * multiplier)).toFixed(4));
          } else if (field === "computed_amount" && rate && computed) {
            nextRow.base_amount = Number((computed / (rate * multiplier)).toFixed(2));
          } else if (amount && computed && !rate) {
            nextRow.exchange_rate = Number((computed / (amount * multiplier)).toFixed(4));
          } else if (rate && computed && !amount) {
            nextRow.base_amount = Number((computed / (rate * multiplier)).toFixed(2));
          } else if (["has_output_vat", "has_ewt", "ewt_rate"].includes(field) && amount && rate) {
            nextRow.computed_amount = Number((amount * rate * multiplier).toFixed(2));
          }
        };

        if (field === "currency") {
          nextRow.currency = String(value || "PHP").toUpperCase();
          if (nextRow.currency === "PHP") nextRow.exchange_rate = 1;
        }

        if (field === "exchange_rate") {
          nextRow.exchange_rate = value === "" ? "" : Number(value);
        }

        if (["computed_amount", "base_amount", "exchange_rate", "currency", "has_output_vat", "has_ewt", "ewt_rate"].includes(field)) {
          autoComputeMissingField();
        }

        return nextRow;
      })
    );
  };

  const removeBillingChargeRow = (index) => {
    setBillingChargeRows((rows) => rows.filter((_, rowIndex) => rowIndex !== index));
  };

  // Receipted charge row handlers
  const addReceiptedChargeRow = () => {
    setReceiptedChargeRows((rows) => [...rows, createReceiptedChargeRow()]);
  };

  const updateReceiptedChargeRow = (index, field, value) => {
    setReceiptedChargeRows((rows) =>
      rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row))
    );
  };

  const removeReceiptedChargeRow = (index) => {
    setReceiptedChargeRows((rows) => rows.filter((_, rowIndex) => rowIndex !== index));
  };

  const appendReceiptedSourceRows = (sourceRows = []) => {
    const nextRows = sourceRows
      .map(createReceiptedChargeRow)
      .filter(hasMeaningfulChargeRow);

    if (!nextRows.length) {
      showErrorModal("No receipted charge source rows found to add.");
      return;
    }

    setReceiptedChargeRows((rows) => {
      const meaningfulRows = rows.filter(hasMeaningfulChargeRow);
      const seen = new Set(meaningfulRows.map(buildReceiptedSourceKey));
      const appended = nextRows.filter((row) => {
        const key = buildReceiptedSourceKey(row);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      if (!appended.length) return meaningfulRows.length ? rows : [createReceiptedChargeRow()];
      return meaningfulRows.length ? [...rows, ...appended] : appended;
    });
  };

  const addReceiptedRequestGroup = (group) => appendReceiptedSourceRows(group?.rows || []);
  const addReceiptedLiquidationGroup = (group) => appendReceiptedSourceRows(group?.rows || []);

  const receiptedSourceKeys = useMemo(
    () => new Set(receiptedChargeRows.filter(hasMeaningfulChargeRow).map(buildReceiptedSourceKey)),
    [receiptedChargeRows]
  );

  const isReceiptedSourceAdded = useCallback(
    (row) => receiptedSourceKeys.has(buildReceiptedSourceKey(row)),
    [receiptedSourceKeys]
  );

  const removeReceiptedSourceRows = useCallback((sourceRows = []) => {
    const targetKeys = new Set(
      sourceRows.map(createReceiptedChargeRow).filter(hasMeaningfulChargeRow).map(buildReceiptedSourceKey)
    );

    if (!targetKeys.size) return;

    setReceiptedChargeRows((rows) => {
      const remaining = rows.filter((row) => !targetKeys.has(buildReceiptedSourceKey(row)));
      return remaining.length ? remaining : [createReceiptedChargeRow()];
    });
  }, []);

  // Actual cost row handlers
  const appendActualCostSourceRows = (sourceRows = []) => {
    const nextRows = sourceRows
      .map(createActualCostRow)
      .filter(hasMeaningfulChargeRow);

    if (!nextRows.length) {
      showErrorModal("No actual cost source rows found to add.");
      return;
    }

    setActualCostRows((rows) => {
      const meaningfulRows = rows.filter(hasMeaningfulChargeRow);
      const seen = new Set(meaningfulRows.map(buildActualCostSourceKey));
      const appended = nextRows.filter((row) => {
        const key = buildActualCostSourceKey(row);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      if (!appended.length) return meaningfulRows.length ? rows : [createActualCostRow()];
      return meaningfulRows.length ? [...rows, ...appended] : appended;
    });
  };

  const addActualCostRequestGroup = (group) =>
    appendActualCostSourceRows((group?.rows || []).map(createActualCostRowFromRequest));
  const addActualCostLiquidationGroup = (group) =>
    appendActualCostSourceRows((group?.rows || []).map(createActualCostRowFromLiquidation));

  const actualCostSourceKeys = useMemo(
    () => new Set(actualCostRows.filter(hasMeaningfulChargeRow).map(buildActualCostSourceKey)),
    [actualCostRows]
  );

  const isActualCostSourceAdded = useCallback(
    (row) => actualCostSourceKeys.has(buildActualCostSourceKey(row)),
    [actualCostSourceKeys]
  );

  const removeActualCostSourceRows = useCallback((sourceRows = []) => {
    const targetKeys = new Set(
      sourceRows.map(createActualCostRow).filter(hasMeaningfulChargeRow).map(buildActualCostSourceKey)
    );

    if (!targetKeys.size) return;

    setActualCostRows((rows) => {
      const remaining = rows.filter((row) => !targetKeys.has(buildActualCostSourceKey(row)));
      return remaining.length ? remaining : [createActualCostRow()];
    });
  }, []);

  const addActualCostRow = () => {
    setActualCostRows((rows) => [...rows, createActualCostRow()]);
  };

  const updateActualCostRow = (index, field, value) => {
    setActualCostRows((rows) =>
      rows.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row))
    );
  };

  const removeActualCostRow = (index) => {
    setActualCostRows((rows) => rows.filter((_, rowIndex) => rowIndex !== index));
  };

  const seedActualFromFixedCharges = () => {
    const rows = joFixedChargeActualRows.length ? joFixedChargeActualRows : [];
    if (!rows.length) {
      showErrorModal("No fixed charge request found in this JO.");
      return;
    }

    setActualCostRows(rows.map((row) => ({ ...row })));
  };

  // Calculate billing amount with VAT and EWT
  const calculateBillingAmount = useCallback((row) => {
    if (row?.computed_amount !== "" && row?.computed_amount !== null && row?.computed_amount !== undefined) {
      return Number(row.computed_amount || 0);
    }
    const currency = String(row?.currency || "PHP").toUpperCase();
    const exchangeRate = currency === "USD" ? Number(row?.exchange_rate || 1) || 1 : 1;
    let amount = Number(row.base_amount || 0) * exchangeRate;
    if (row.has_output_vat) {
      amount = amount * 1.12;
    }
    if (row.has_ewt && row.ewt_rate) {
      const ewtPercent = parseFloat(row.ewt_rate) / 100;
      amount = amount * (1 + ewtPercent);
    }
    return amount;
  }, []);

  const billingChargesSubtotal = useMemo(
    () => billingChargeRows.reduce((sum, row) => sum + calculateBillingAmount(row), 0),
    [billingChargeRows, calculateBillingAmount]
  );

  const billingAmountColumnLabel = useMemo(() => {
    const currencies = [...new Set(
      billingChargeRows.map((row) => String(row?.currency || "PHP").toUpperCase())
    )];
    return currencies.length === 1 ? `Amount in ${currencies[0]}` : "Amount by Currency";
  }, [billingChargeRows]);

  const billingChargeBreakdown = useMemo(() => {
    const summary = billingChargeRows.reduce(
      (totals, row) => {
        const currency = String(row?.currency || "PHP").toUpperCase();
        const exchangeRate = currency === "USD" ? Number(row?.exchange_rate || 1) || 1 : 1;
        const baseAmount = Number(row?.base_amount || 0) * exchangeRate;
        const vatAmount = row?.has_output_vat ? baseAmount * 0.12 : 0;
        const rate = Number.parseFloat(row?.ewt_rate || 0);
        const hasEwt = Boolean(row?.has_ewt && rate > 0);
        const calculatedBeforeEwt = baseAmount + vatAmount;
        const calculatedAmount = calculateBillingAmount(row);
        const hasExplicitComputedAmount = row?.computed_amount !== "" && row?.computed_amount !== null && row?.computed_amount !== undefined;
        const ewtAmount = hasEwt
          ? Math.max(
              hasExplicitComputedAmount
                ? calculatedAmount - calculatedAmount / (1 + rate / 100)
                : calculatedBeforeEwt * (rate / 100),
              0
            )
          : 0;

        totals.vatExclusive += baseAmount;
        totals.vat += vatAmount;
        if (vatAmount > 0) totals.hasVat = true;
        if (ewtAmount > 0) {
          const rateKey = String(rate);
          totals.ewtByRate[rateKey] = (totals.ewtByRate[rateKey] || 0) + ewtAmount;
          totals.ewt += ewtAmount;
        }
        return totals;
      },
      { vatExclusive: 0, vat: 0, ewt: 0, ewtByRate: {}, hasVat: false }
    );

    return summary;
  }, [billingChargeRows, calculateBillingAmount]);

  const receiptedChargesSubtotal = useMemo(
    () => receiptedChargeRows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [receiptedChargeRows]
  );

  const grandTotal = billingChargesSubtotal + receiptedChargesSubtotal;

  const actualCostTotal = useMemo(
    () => actualCostRows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [actualCostRows]
  );

  const grossProfit = grandTotal - actualCostTotal;

  const previewReportData = useMemo(
    () => ({
      ...(savedReport || {}),
      report_date_created: reportDateCreated || savedReport?.report_date_created || new Date().toISOString().slice(0, 10),
      consignee: reportPayee || "",
      bill_to: billToSameAsConsignee ? reportPayee || "" : reportBillTo || "",
      bill_to_same_as_consignee: billToSameAsConsignee,
      job_order: modalData?.job_order || savedReport?.job_order || "",
      tracking_no: shipmentDetails?.tracking_no || savedReport?.tracking_no || "",
      shipment_details: shipmentDetails?.shipment_description || savedReport?.shipment_details || "",
      dock: reportDock || savedReport?.dock || "",
      po_no: reportPoNo || savedReport?.po_no || "",
      reference_no: reportReferenceNo || savedReport?.reference_no || "",
      delivery_date: reportDeliveryDate || savedReport?.delivery_date || "",
      status: currentStage,
      initial_approver_name: initialApproverName,
      final_approver_name: finalApproverName,
      team_leader_user_id: selectedTeamLeaderId || savedReport?.team_leader_user_id || costAnalysis?.team_leader_user_id || "",
      team_leader_name: selectedTeamLeaderName,
      billing_charge_rows: billingChargeRows,
      receipted_charge_rows: receiptedChargeRows,
      actual_cost_rows: actualCostRows,
      billing_charges_subtotal: billingChargesSubtotal,
      receipted_charges_subtotal: receiptedChargesSubtotal,
      grand_total: grandTotal,
      actual_cost_total: actualCostTotal,
      gross_profit: grossProfit,
    }),
    [
      actualCostTotal,
      actualCostRows,
      billingChargesSubtotal,
      billingChargeRows,
      costAnalysis?.team_leader_user_id,
      currentStage,
      finalApproverName,
      grandTotal,
      initialApproverName,
      modalData?.job_order,
      receiptedChargeRows,
      receiptedChargesSubtotal,
      reportDateCreated,
      reportDock,
      reportDeliveryDate,
      reportPayee,
      reportBillTo,
      reportPoNo,
      reportReferenceNo,
      savedReport,
      selectedTeamLeaderId,
      selectedTeamLeaderName,
      shipmentDetails,
      grossProfit,
      billToSameAsConsignee,
    ]
  );

  // Use backend-provided payee label, with fallback to utility function for backward compatibility
  const joPayeeLabel = modalData?.jo_payee_label || getCostAnalysisPayeeLabel(shipmentDetails);

  const activeJoTransactions = useMemo(
    () => (activeGroupRequests.length ? activeGroupRequests : modalData ? [modalData] : []),
    [activeGroupRequests, modalData]
  );
  const joRequests = useMemo(
    () => (joLedgerDetail?.requests?.length ? joLedgerDetail.requests : activeJoTransactions),
    [activeJoTransactions, joLedgerDetail?.requests]
  );
  const joRequestParticularSourceRows = useMemo(() => {
    const seen = new Set();
    return joRequests
      .flatMap((request) =>
        (request?.particulars || [])
          .filter((particular) => hasMeaningfulChargeRow(particular))
          .map((particular) =>
            createReceiptedChargeRowFromRequest({
              ...particular,
              request_id: request?.id || "",
              source_request_id: request?.id || "",
              source_rfp_no: request?.rfp_no || "",
              source_payee: request?.payee || "",
              amount: particular?.total ?? particular?.amount ?? 0,
            })
          )
      )
      .filter((row) => {
        const key = buildReceiptedSourceKey(row);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [joRequests]);
  const joRequestParticularGroups = useMemo(() => {
    const grouped = new Map();
    joRequestParticularSourceRows.forEach((row) => {
      const key = row.source_request_id || row.source_rfp_no || row.id;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          rfpNo: row.source_rfp_no || "—",
          payee: row.source_payee || "",
          rows: [],
        });
      }
      grouped.get(key).rows.push(row);
    });
    return Array.from(grouped.values()).map((group) => ({
      ...group,
      rowCount: group.rows.length,
      fixedChargeCount: (joRequests.find((request) => (request?.id || "") === group.key)?.particulars || []).filter(
        (particular) => isFixedChargeParticular(particular) && hasMeaningfulChargeRow(particular)
      ).length,
      totalAmount: group.rows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    }));
  }, [joRequestParticularSourceRows, joRequests]);
  const joLiquidationActualCostRows = useMemo(
    () =>
      joRequests.flatMap((request) =>
        request?.for_liquidation && Array.isArray(request?.liquidation_report?.actual_cost_rows)
          ? request.liquidation_report.actual_cost_rows.map((row) => ({
              ...row,
              source_request_id: request?.id || "",
              source_rfp_no: request?.rfp_no || "",
              liquidation_no: request?.liquidation?.liquidation_no || request?.liquidation_report?.liquidation_no || "",
              liquidation_id: request?.liquidation?.id || "",
              liquidation_status:
                request?.liquidation?.status ||
                request?.liquidation_report?.status ||
                "",
            }))
          : []
      ),
    [joRequests]
  );
  const joLiquidationSourceRows = useMemo(() => {
    const seen = new Set();
    return joLiquidationActualCostRows
      .map(createReceiptedChargeRowFromLiquidation)
      .filter((row) => {
        const key = buildReceiptedSourceKey(row);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [joLiquidationActualCostRows]);
  const joLiquidationSourceGroups = useMemo(() => {
    const grouped = new Map();
    joLiquidationSourceRows.forEach((row) => {
      const key = `${row.liquidation_id || row.liquidation_no || row.source_rfp_no}-${row.source_rfp_no}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          rfpNo: row.source_rfp_no || "—",
          liquidationNo: row.liquidation_no || row.liquidation_id || "—",
          liquidationStatus: row.liquidation_status || "",
          rows: [],
        });
      }
      grouped.get(key).rows.push(row);
    });
    return Array.from(grouped.values()).map((group) => ({
      ...group,
      rowCount: group.rows.length,
      totalAmount: group.rows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    }));
  }, [joLiquidationSourceRows]);
  const joLiquidations = useMemo(
    () => joRequests.filter((request) => request.liquidation_report || request.liquidation),
    [joRequests]
  );
  const joVouchers = useMemo(
    () => joLedgerDetail?.check_vouchers || [],
    [joLedgerDetail?.check_vouchers]
  );

  // Unified catalog of every document that can be attached to the downloaded report.
  const attachableDocuments = useMemo(() => {
    const trackNTrace = shipmentDocuments.map((doc) => ({
      key: `shipment-doc-${doc.id}`,
      category: "track_n_trace",
      title: doc.document_title || doc.document_name || "Untitled document",
      subtitle: doc.document_name || "",
      url: doc.document_url || "",
      file_type: doc.document_name || "",
    }));

    const rfps = joRequests.map((req, index) => ({
      key: `rfp-${req.id || index}`,
      category: "accounting",
      title: `RFP ${req.rfp_no || "—"}`,
      subtitle: req.payee || "",
      url: (req.attachments || [])[0]?.file_url || "",
      file_type: (req.attachments || [])[0]?.file_type || (req.attachments || [])[0]?.file_name || "",
    }));

    const liquidations = joLiquidations.map((req, index) => ({
      key: `liq-${req.id || index}`,
      category: "accounting",
      title: `Liquidation ${req.liquidation_report?.liquidation_no || req.liquidation?.liquidation_no || req.rfp_no || "—"}`,
      subtitle: req.payee || "",
      url: req.liquidation?.return_receipt_file_path || "",
      file_type: "",
    }));

    const vouchers = joVouchers.map((voucher, index) => ({
      key: `voucher-${voucher.id || index}`,
      category: "accounting",
      title: `Voucher ${voucher.voucher_no || "—"}`,
      subtitle: voucher.payee || "",
      url: "",
      file_type: "",
    }));

    return [...trackNTrace, ...rfps, ...liquidations, ...vouchers];
  }, [shipmentDocuments, joRequests, joLiquidations, joVouchers]);

  const selectedAttachedDocuments = useMemo(() => {
    const documentsByKey = new Map();

    (savedReport?.attached_documents || []).forEach((document) => {
      if (document?.key) documentsByKey.set(document.key, document);
    });
    attachableDocuments.forEach((document) => {
      if (document?.key) documentsByKey.set(document.key, document);
    });

    return Array.from(selectedAttachmentKeys)
      .map((key) => documentsByKey.get(key))
      .filter(Boolean);
  }, [attachableDocuments, savedReport?.attached_documents, selectedAttachmentKeys]);

  const toggleAttachmentSelected = useCallback((key) => {
    setSelectedAttachmentKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const selectedRequestDoc = useMemo(
    () => joRequests[Math.min(previewSelection.requests, Math.max(joRequests.length - 1, 0))] || null,
    [joRequests, previewSelection.requests]
  );
  const selectedLiquidationDoc = useMemo(
    () => joLiquidations[Math.min(previewSelection.liquidations, Math.max(joLiquidations.length - 1, 0))] || null,
    [joLiquidations, previewSelection.liquidations]
  );
  const selectedVoucherDoc = useMemo(
    () => joVouchers[Math.min(previewSelection.vouchers, Math.max(joVouchers.length - 1, 0))] || null,
    [joVouchers, previewSelection.vouchers]
  );
  const selectedCostRequestDoc = useMemo(
    () => joRequests.find((row) => row.cost_analysis_report) || joRequests[0] || null,
    [joRequests]
  );
  const selectedVoucherLines = useMemo(
    () => (selectedVoucherDoc ? voucherLinesById[selectedVoucherDoc.id] || [] : []),
    [selectedVoucherDoc, voucherLinesById]
  );

  const joFixedChargeActualRows = useMemo(
    () =>
      joRequests.flatMap((request) =>
        (request?.particulars || [])
          .filter((particular) => isFixedChargeParticular(particular) && hasMeaningfulChargeRow(particular))
          .map((particular) =>
            createActualCostRowFromFixedCharges({
              ...particular,
              request_id: request?.id || "",
              source_request_id: request?.id || "",
              source_rfp_no: request?.rfp_no || "",
              source_payee: request?.payee || "",
              liquidation_no:
                request?.liquidation?.liquidation_no || request?.liquidation_report?.liquidation_no || "",
              liquidation_id: request?.liquidation?.id || "",
              liquidation_status:
                request?.liquidation?.status ||
                request?.liquidation_report?.status ||
                "",
            })
          )
      ),
    [joRequests]
  );

  useEffect(() => {
    if (!isModalOpen) return;
    if (savedReport?.actual_cost_rows?.some(hasMeaningfulActualCostRow)) return;
    if (!joFixedChargeActualRows.length) return;
    if (actualCostRows.some(hasMeaningfulActualCostRow)) return;

    console.debug("[CostAnalysis] seeding actual cost rows from JO fixed charges", {
      count: joFixedChargeActualRows.length,
    });
    setActualCostRows(joFixedChargeActualRows);
  }, [actualCostRows, isModalOpen, joFixedChargeActualRows, savedReport?.actual_cost_rows]);

  useEffect(() => {
    if (previewDocType !== "vouchers" || !selectedVoucherDoc?.id || voucherLinesById[selectedVoucherDoc.id]) return undefined;

    let cancelled = false;
    const loadVoucherLines = async () => {
      try {
        const token = localStorage.getItem("authToken");
        console.debug("[CostAnalysis] fetching voucher lines", {
          voucherId: selectedVoucherDoc.id,
          tokenPresent: Boolean(token),
          url: `${API_BASE_URL}/api/accounting/check-vouchers/${selectedVoucherDoc.id}/lines`,
        });
        const res = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/${selectedVoucherDoc.id}/lines`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        console.debug("[CostAnalysis] voucher lines status", {
          voucherId: selectedVoucherDoc.id,
          status: res.status,
          ok: res.ok,
          contentType: res.headers.get("content-type"),
        });
        const json = await readJsonResponse(res);
        console.debug("[CostAnalysis] voucher lines payload", {
          voucherId: selectedVoucherDoc.id,
          success: json.success,
          count: Array.isArray(json.data) ? json.data.length : 0,
          first: Array.isArray(json.data) ? json.data[0] : null,
        });
        if (!cancelled && json.success) {
          setVoucherLinesById((prev) => ({
            ...prev,
            [selectedVoucherDoc.id]: json.data || [],
          }));
        }
      } catch (err) {
        console.error("Failed to load voucher preview lines", err);
      }
    };

    loadVoucherLines();
    return () => {
      cancelled = true;
    };
  }, [previewDocType, selectedVoucherDoc?.id, voucherLinesById]);

  const requestPreviewHtml = useMemo(() => {
    if (!selectedRequestDoc) return "";
    return buildRequestVoucherHtml({
      item: selectedRequestDoc,
      shipment: shipmentDetails,
    });
  }, [selectedRequestDoc, shipmentDetails]);

  const liquidationPreviewHtml = useMemo(() => {
    if (!selectedLiquidationDoc?.liquidation_report) return "";
    const report = selectedLiquidationDoc.liquidation_report;
    return buildLiquidationDocumentHtml({
      request: selectedLiquidationDoc,
      reportData: report,
      shipment: shipmentDetails,
      reportDateCreatedValue: report.report_date_created || selectedLiquidationDoc.created_at,
      dockValue: report.dock || "",
      poNoValue: report.po_no || "",
      referenceNoValue: report.reference_no || "",
      deliveryDateValue: report.delivery_date || "",
      actualRows: report.actual_cost_rows || [],
      advanceRowsValue: report.advance_rows || [],
      actualCostTotalValue: report.total_actual_cost_incurred || 0,
      advancesTotalValue: report.total_advances || 0,
      reimbursementTotalValue: report.total_reimbursement_return || 0,
    });
  }, [selectedLiquidationDoc, shipmentDetails]);

  const voucherPreviewHtml = useMemo(() => {
    if (!selectedVoucherDoc) return "";
    const relatedRequests = joRequests.filter((request) => (selectedVoucherDoc.request_ids || []).includes(request.id));
    return buildCheckVoucherPrintHtml({
      voucher: selectedVoucherDoc,
      requests: relatedRequests,
      lines: selectedVoucherLines,
    });
  }, [joRequests, selectedVoucherDoc, selectedVoucherLines]);

  const costAnalysisPreviewHtml = useMemo(() => {
    if (!selectedCostRequestDoc) return "";
    const approvedRequests = joRequests.filter((req) => req.status === "approved");
    const savedPreviewReport = selectedCostRequestDoc.cost_analysis_report || previewReportData;
    const previewStage = selectedCostRequestDoc.cost_analysis_stage || currentStage;
    const reportData = {
      ...savedPreviewReport,
      status: previewStage,
      initial_approver_name: savedPreviewReport.initial_approver_name || initialApproverName,
      final_approver_name: savedPreviewReport.final_approver_name || finalApproverName,
    };
    return buildCostAnalysisReportHtml({
      request: selectedCostRequestDoc,
      reportData,
      billingRows: selectedCostRequestDoc.cost_analysis_report?.billing_charge_rows || billingChargeRows,
      receiptedRows: selectedCostRequestDoc.cost_analysis_report?.receipted_charge_rows || receiptedChargeRows,
      actualRows: selectedCostRequestDoc.cost_analysis_report?.actual_cost_rows || actualCostRows,
      billingSubtotal: selectedCostRequestDoc.cost_analysis_report?.billing_charges_subtotal || billingChargesSubtotal,
      receiptedSubtotal: selectedCostRequestDoc.cost_analysis_report?.receipted_charges_subtotal || receiptedChargesSubtotal,
      grandTotalValue: selectedCostRequestDoc.cost_analysis_report?.grand_total || grandTotal,
      actualCostTotalValue: selectedCostRequestDoc.cost_analysis_report?.actual_cost_total || actualCostTotal,
      grossProfitValue: selectedCostRequestDoc.cost_analysis_report?.gross_profit || grossProfit,
      calculateBillingAmount,
      connectedRequests: approvedRequests,
    });
  }, [
    actualCostRows,
    actualCostTotal,
    billingChargeRows,
    billingChargesSubtotal,
    calculateBillingAmount,
    currentStage,
    finalApproverName,
    grandTotal,
    grossProfit,
    initialApproverName,
    joRequests,
    receiptedChargeRows,
    receiptedChargesSubtotal,
    selectedCostRequestDoc,
    previewReportData,
  ]);

  const saveReport = async () => {
    if (!activeId) return;
    setReportLoading(true);

    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/${activeId}/report`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          report_date_created: reportDateCreated,
          consignee: reportPayee,
          bill_to: billToSameAsConsignee ? reportPayee : reportBillTo,
          bill_to_same_as_consignee: billToSameAsConsignee,
          job_order: modalData?.job_order || "",
          tracking_no: shipmentDetails?.tracking_no || "",
          shipment_details: shipmentDetails?.shipment_description || "",
          dock: reportDock,
          po_no: reportPoNo,
          reference_no: reportReferenceNo,
          delivery_date: reportDeliveryDate,
          team_leader_user_id: selectedTeamLeaderId,
          billing_charge_rows: billingChargeRows,
          receipted_charge_rows: receiptedChargeRows,
          actual_cost_rows: actualCostRows,
          attached_documents: selectedAttachedDocuments,
          revision_edit: true,
        }),
      });

      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Failed to save cost analysis report");

      const persistedReport = json.data?.cost_analysis_report;
      if (!persistedReport || persistedReport.error) {
        throw new Error("The cost analysis report was saved, but its attached documents could not be confirmed.");
      }

      const expectedAttachmentKeys = selectedAttachedDocuments.map((document) => document.key).sort();
      const persistedAttachmentKeys = (persistedReport.attached_documents || [])
        .map((document) => document.key)
        .filter(Boolean)
        .sort();
      if (JSON.stringify(expectedAttachmentKeys) !== JSON.stringify(persistedAttachmentKeys)) {
        throw new Error("The selected attached documents were not saved. Please try again.");
      }

      setDetail((current) =>
        current
          ? {
              ...current,
              cost_analysis: json.data?.cost_analysis || current.cost_analysis,
              cost_analysis_stage: json.data?.cost_analysis?.status || current.cost_analysis_stage,
              cost_analysis_report: persistedReport,
            }
          : current
      );
      setSelectedAttachmentKeys(new Set(persistedAttachmentKeys));

      showSuccessModal(
        json.data?.requires_reapproval ? "Changes Saved — Re-approval Required" : "Report Saved",
        json.data?.requires_reapproval
          ? "The cost analysis changes were saved and the report returned to Draft. Submit it again for User 130 and User 127 approval."
          : `Cost analysis report and ${persistedAttachmentKeys.length} selected document${persistedAttachmentKeys.length === 1 ? "" : "s"} have been saved successfully.`
      );
      await refreshList(activeId);
    } catch (err) {
      showErrorModal(err.message || "Failed to save cost analysis report");
    } finally {
      setReportLoading(false);
    }
  };

  // Lets approvers update which documents are attached to the report without
  // re-submitting the full form (which requires a team leader to be selected).
  const saveAttachedDocuments = async () => {
    if (!activeId) return;

    setSavingAttachments(true);

    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/${activeId}/report`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          report_date_created: reportDateCreated,
          consignee: reportPayee,
          bill_to: billToSameAsConsignee ? reportPayee : reportBillTo,
          bill_to_same_as_consignee: billToSameAsConsignee,
          job_order: modalData?.job_order || "",
          tracking_no: shipmentDetails?.tracking_no || "",
          shipment_details: shipmentDetails?.shipment_description || "",
          dock: reportDock,
          po_no: reportPoNo,
          reference_no: reportReferenceNo,
          delivery_date: reportDeliveryDate,
          team_leader_user_id: selectedTeamLeaderId || savedReport?.team_leader_user_id || "",
          billing_charge_rows: billingChargeRows,
          receipted_charge_rows: receiptedChargeRows,
          actual_cost_rows: actualCostRows,
          attached_documents: selectedAttachedDocuments,
          revision_edit: false,
        }),
      });

      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Failed to update attached documents");

      const persistedReport = json.data?.cost_analysis_report;
      if (!persistedReport || persistedReport.error) {
        throw new Error("The attached documents were saved, but the updated selection could not be confirmed.");
      }

      const expectedAttachmentKeys = selectedAttachedDocuments.map((document) => document.key).sort();
      const persistedAttachmentKeys = (persistedReport.attached_documents || [])
        .map((document) => document.key)
        .filter(Boolean)
        .sort();
      if (JSON.stringify(expectedAttachmentKeys) !== JSON.stringify(persistedAttachmentKeys)) {
        throw new Error("The selected attached documents were not saved. Please try again.");
      }

      setDetail((current) =>
        current
          ? {
              ...current,
              cost_analysis_report: persistedReport,
            }
          : current
      );
      setSelectedAttachmentKeys(new Set(persistedAttachmentKeys));

      showSuccessModal(
        "Attachments Updated",
        `${persistedAttachmentKeys.length} selected document${persistedAttachmentKeys.length === 1 ? "" : "s"} saved for this cost analysis.`
      );
      await refreshList(activeId);
    } catch (err) {
      showErrorModal(err.message || "Failed to update attached documents");
    } finally {
      setSavingAttachments(false);
    }
  };

  const submitForApproval = async () => {
    if (!activeId) return;

    setActionLoading("submit");

    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/${activeId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "submit" }),
      });

      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Failed to submit cost analysis");

      showSuccessModal("Submitted", "Cost analysis has been submitted for approval.");
      await refreshList(activeId);
    } catch (err) {
      showErrorModal(err.message || "Failed to submit cost analysis");
    } finally {
      setActionLoading("");
    }
  };

  const verifyAnalysis = async () => {
    if (!activeId) return;

    setActionLoading("approve_initial");

    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/${activeId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "approve_initial" }),
      });

      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Failed to approve cost analysis");

      showSuccessModal("Partially Approved", "Cost analysis has been partially approved and is awaiting final approval.");
      await refreshList(activeId);
    } catch (err) {
      showErrorModal(err.message || "Failed to approve cost analysis");
    } finally {
      setActionLoading("");
    }
  };

  const approveAnalysis = async () => {
    if (!activeId) return;

    setActionLoading("approve");

    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/${activeId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "approve" }),
      });

      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Failed to approve cost analysis");

      showSuccessModal("Approved", "Cost analysis has been approved.");
      await refreshList(activeId);
    } catch (err) {
      showErrorModal(err.message || "Failed to approve cost analysis");
    } finally {
      setActionLoading("");
    }
  };

  const rejectAnalysis = async () => {
    if (!activeId) return;
    if (!rejectReason.trim()) {
      showErrorModal("Please provide a reason for rejection.");
      return;
    }

    setActionLoading("reject");

    try {
      const res = await authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/${activeId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "reject", reason: rejectReason }),
      });

      const json = await readJsonResponse(res);
      if (!json.success) throw new Error(json.message || "Failed to reject cost analysis");

      showSuccessModal("Rejected", "Cost analysis has been rejected.");
      setRejectReason("");
      await refreshList(activeId);
    } catch (err) {
      showErrorModal(err.message || "Failed to reject cost analysis");
    } finally {
      setActionLoading("");
    }
  };

  const downloadReport = async (row) => {
    if (!row?.cost_analysis_report) return;

    setDownloadLoadingId(row.id);

    try {
      const detailResponse = await authFetch(
        `${API_BASE_URL}/api/accounting/cost-analysis/${row.id}`,
        { credentials: "include" }
      );
      const detailJson = await readJsonResponse(detailResponse);
      if (!detailJson.success) throw new Error(detailJson.message || "Failed to load report details");

      const downloadDetail = detailJson.data || row;
      const downloadShipment = downloadDetail.shipment_details || shipmentDetails;
      let downloadRequests = [downloadDetail];
      let downloadVouchers = [];

      if (downloadDetail.job_order) {
        const ledgerResponse = await authFetch(
          `${API_BASE_URL}/api/accounting/jo-ledger/${encodeURIComponent(downloadDetail.job_order)}`,
          { credentials: "include" }
        );
        const ledgerJson = await readJsonResponse(ledgerResponse);
        if (ledgerJson.success) {
          downloadRequests = ledgerJson.data?.requests?.length
            ? ledgerJson.data.requests
            : downloadRequests;
          downloadVouchers = ledgerJson.data?.check_vouchers || [];
        }
      }

      const downloadLiquidations = downloadRequests.filter(
        (request) => request.liquidation_report || request.liquidation
      );
      const persistedDocuments = downloadDetail.cost_analysis_report?.attached_documents || [];
      const documentsToDownload = persistedDocuments.length
        ? persistedDocuments
        : selectedAttachedDocuments;

      const selectedDocumentsWithHtml = await Promise.all(
        documentsToDownload.map(async (document) => {
          if (document.category !== "accounting") return document;

          if (document.key.startsWith("rfp-")) {
            const request = downloadRequests.find((item, index) => `rfp-${item.id || index}` === document.key);
            return request
              ? { ...document, html: buildRequestVoucherHtml({ item: request, shipment: downloadShipment }) }
              : document;
          }

          if (document.key.startsWith("liq-")) {
            const request = downloadLiquidations.find(
              (item, index) => `liq-${item.id || index}` === document.key
            );
            const report = request?.liquidation_report;
            return report
              ? {
                  ...document,
                  html: buildLiquidationDocumentHtml({
                    request,
                    reportData: report,
                    shipment: downloadShipment,
                    reportDateCreatedValue: report.report_date_created || request.created_at,
                    dockValue: report.dock || "",
                    poNoValue: report.po_no || "",
                    referenceNoValue: report.reference_no || "",
                    deliveryDateValue: report.delivery_date || "",
                    actualRows: report.actual_cost_rows || [],
                    advanceRowsValue: report.advance_rows || [],
                    actualCostTotalValue: report.total_actual_cost_incurred || 0,
                    advancesTotalValue: report.total_advances || 0,
                    reimbursementTotalValue: report.total_reimbursement_return || 0,
                  }),
                }
              : document;
          }

          if (document.key.startsWith("voucher-")) {
            const voucher = downloadVouchers.find(
              (item, index) => `voucher-${item.id || index}` === document.key
            );
            if (!voucher) return document;

            let lines = voucherLinesById[voucher.id] || [];
            if (voucher.id && !voucherLinesById[voucher.id]) {
              const response = await authFetch(
                `${API_BASE_URL}/api/accounting/check-vouchers/${voucher.id}/lines`,
                { credentials: "include" }
              );
              const json = await readJsonResponse(response);
              if (json.success) lines = json.data || [];
            }

            const relatedRequests = downloadRequests.filter((request) =>
              (voucher.request_ids || []).includes(request.id)
            );
            return {
              ...document,
              html: buildCheckVoucherPrintHtml({ voucher, requests: relatedRequests, lines }),
            };
          }

          return document;
        })
      );

      const res = await authFetch(
        `${API_BASE_URL}/api/accounting/cost-analysis/${row.id}/report/pdf`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attached_documents: selectedDocumentsWithHtml }),
        }
      );

      if (!res.ok) {
        const json = await readJsonResponse(res).catch(() => null);
        throw new Error(json?.message || "Failed to generate report");
      }

      const blob = await res.blob();
      const fileName = `cost-analysis-${row.rfp_no || row.job_order || row.id}.pdf`.replace(
        /[^a-zA-Z0-9._-]+/g,
        "_"
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to generate report", err);
      showErrorModal(err.message || "Failed to generate report");
    } finally {
      setDownloadLoadingId("");
    }
  };

  const openModal = (row) => {
    const jobOrder = String(row?.job_order || "").trim();
    // Only group by job order if a job order exists, otherwise treat as individual request
    const group = jobOrder
      ? requests.filter((item) => normalizeJobOrder(item?.job_order) === normalizeJobOrder(jobOrder))
      : [row];
    const preferredRequest =
      group.find((item) => item.cost_analysis_report?.id || item.cost_analysis?.id) ||
      group[0] ||
      row ||
      null;

    setActiveJobOrder(jobOrder);
    setActiveId(preferredRequest?.id || row?.id || "");
    setIsModalOpen(true);
    setModalMode("preview");
    setPreviewDocType("requests");
    setPreviewZoom(1);
    resetModalFields();
  };

  const openReportEditor = (row) => {
    const jobOrder = String(row?.job_order || "").trim();
    const group = jobOrder
      ? requests.filter((item) => normalizeJobOrder(item?.job_order) === normalizeJobOrder(jobOrder))
      : [row];
    const preferredRequest =
      group.find((item) => item.cost_analysis_report?.id || item.cost_analysis?.id) ||
      group[0] ||
      row ||
      null;

    setActiveJobOrder(jobOrder);
    setActiveId(preferredRequest?.id || row?.id || "");
    setIsModalOpen(true);
    setModalMode("editor");
    setPreviewDocType("requests");
    resetModalFields();
  };

  const openAttachmentModal = async (row) => {
    const costAnalysisId = row?.cost_analysis?.id;
    if (!costAnalysisId) {
      showErrorModal("Cost analysis not found. Please create a cost analysis report first.");
      return;
    }
    setAttachmentModalData({ ...row, costAnalysisId });
    setAttachmentModalOpen(true);
    setAttachmentsLoading(true);
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/accounting/cost-analysis/${costAnalysisId}/attachments`,
        { credentials: "include" }
      );
      const json = await readJsonResponse(res);
      if (json.success) {
        setAttachments(json.data || []);
      }
    } catch (err) {
      console.error("Failed to load attachments", err);
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const closeAttachmentModal = () => {
    setAttachmentModalOpen(false);
    setAttachmentModalData(null);
    setAttachments([]);
  };

  const handleAttachmentUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length || !attachmentModalData?.costAnalysisId) return;

    setUploadingAttachments(true);
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append("files", file));

    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/accounting/cost-analysis/${attachmentModalData.costAnalysisId}/attachments`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );
      const json = await readJsonResponse(res);
      if (json.success) {
        setAttachments((prev) => [...(json.data || []), ...prev]);
      } else {
        showErrorModal(json.message || "Failed to upload attachments");
      }
    } catch (err) {
      console.error("Failed to upload attachments", err);
      showErrorModal(err.message || "Failed to upload attachments");
    } finally {
      setUploadingAttachments(false);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = "";
      }
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm("Are you sure you want to delete this attachment?")) return;

    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/accounting/cost-analysis/attachments/${attachmentId}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      const json = await readJsonResponse(res);
      if (json.success) {
        setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
      } else {
        showErrorModal(json.message || "Failed to delete attachment");
      }
    } catch (err) {
      console.error("Failed to delete attachment", err);
      showErrorModal(err.message || "Failed to delete attachment");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalMode("editor");
    setDetail(null);
    setActiveJobOrder("");
    setPreviewDocType("requests");
    resetModalFields();
  };

  const openAccountingDocPreview = async (type, index) => {
    const previewWindow = window.open("", "_blank");
    if (!previewWindow) {
      showErrorModal("Please allow pop-ups to view this document in a new tab.");
      return;
    }

    previewWindow.opener = null;
    previewWindow.document.write("<!doctype html><html><head><title>Loading document...</title></head><body>Loading document...</body></html>");
    previewWindow.document.close();

    try {
      let html = "";

      if (type === "requests") {
        const request = joRequests[index];
        if (!request) throw new Error("Request document not found");
        html = buildRequestVoucherHtml({ item: request, shipment: shipmentDetails });
      } else if (type === "liquidations") {
        const request = joLiquidations[index];
        const report = request?.liquidation_report;
        if (!request || !report) throw new Error("Liquidation document not found");
        html = buildLiquidationDocumentHtml({
          request,
          reportData: report,
          shipment: shipmentDetails,
          reportDateCreatedValue: report.report_date_created || request.created_at,
          dockValue: report.dock || "",
          poNoValue: report.po_no || "",
          referenceNoValue: report.reference_no || "",
          deliveryDateValue: report.delivery_date || "",
          actualRows: report.actual_cost_rows || [],
          advanceRowsValue: report.advance_rows || [],
          actualCostTotalValue: report.total_actual_cost_incurred || 0,
          advancesTotalValue: report.total_advances || 0,
          reimbursementTotalValue: report.total_reimbursement_return || 0,
        });
      } else if (type === "vouchers") {
        const voucher = joVouchers[index];
        if (!voucher) throw new Error("Voucher document not found");

        let voucherLines = voucherLinesById[voucher.id] || [];
        if (voucher.id && !voucherLinesById[voucher.id]) {
          const token = localStorage.getItem("authToken");
          const response = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/${voucher.id}/lines`, {
            credentials: "include",
            headers: { Authorization: `Bearer ${token}` },
          });
          const json = await readJsonResponse(response);
          if (!json.success) throw new Error(json.message || "Failed to load voucher document");
          voucherLines = json.data || [];
          setVoucherLinesById((previous) => ({ ...previous, [voucher.id]: voucherLines }));
        }

        const relatedRequests = joRequests.filter((request) => (voucher.request_ids || []).includes(request.id));
        html = buildCheckVoucherPrintHtml({
          voucher,
          requests: relatedRequests,
          lines: voucherLines,
        });
      }

      if (!html) throw new Error("Document preview is unavailable");

      previewWindow.document.open();
      previewWindow.document.write(html);
      previewWindow.document.close();
      previewWindow.focus();
    } catch (err) {
      previewWindow.close();
      showErrorModal(err.message || "Failed to open document");
    }
  };

  const printCostAnalysisPreview = () => {
    if (!costAnalysisPreviewHtml) {
      showErrorModal("Cost analysis report is not available for printing.");
      return;
    }

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      showErrorModal("Please allow pop-ups to print the cost analysis report.");
      return;
    }

    printWindow.opener = null;
    printWindow.document.open();
    printWindow.document.write(costAnalysisPreviewHtml);
    printWindow.document.close();
    printWindow.focus();
    printWindow.setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg shadow-blue-200"
              style={{
                background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
              }}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5 text-white"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M6 4.5a1.5 1.5 0 0 1 1.5-1.5h9A1.5 1.5 0 0 1 18 4.5v15A1.5 1.5 0 0 1 16.5 21h-9A1.5 1.5 0 0 1 6 19.5v-15Zm2 1v2h8v-2H8Zm0 4v2h2v-2H8Zm4 0v2h2v-2h-2Zm4 0v2h0.01v-2H16Zm-8 4v2h2v-2H8Zm4 0v2h2v-2h-2Zm4 0v2h0.01v-2H16Z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Cost Analysis</h1>
              <p className="text-sm text-slate-500">Manage cost analysis for approved requests</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={openTemplateModal}
              className="inline-flex items-center gap-2 rounded-full bg-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800"
            >
              <i className="fa-solid fa-file-circle-plus" />
              Create CA Template
            </button>
            {isSuperadmin && (
              <>
              <button
                type="button"
                onClick={() => setScopeFilter("created")}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  scopeFilter === "created"
                    ? "bg-blue text-white shadow-sm"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                My Shipments
              </button>
              <button
                type="button"
                onClick={() => setScopeFilter("all")}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                  scopeFilter === "all"
                    ? "bg-blue text-white shadow-sm"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                See All
              </button>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {[
            { key: "all", value: counts.total, label: "Total", color: "text-slate-900" },
            { key: "awaiting", value: counts.awaiting, label: "Awaiting Entry", color: "text-amber-600" },
            { key: "inProgress", value: counts.inProgress, label: "In Progress", color: "text-blue-600" },
            { key: "partially_approved", value: counts.partiallyApproved, label: "Partially Approved", color: "text-violet-600" },
            { key: "approved", value: counts.approved, label: "Approved", color: "text-emerald-600" },
            { key: "completed", value: counts.completed, label: "Completed", color: "text-slate-600" },
          ].map((card) => {
            const isActive = stageFilter === card.key;
            return (
              <button
                key={card.key}
                type="button"
                onClick={() => setStageFilter((prev) => (prev === card.key ? "all" : card.key))}
                className={`text-left bg-white rounded-2xl p-4 shadow-sm border transition ${
                  isActive
                    ? "border-blue-400 ring-2 ring-blue-100"
                    : "border-slate-100 hover:border-slate-200 hover:shadow-md"
                }`}
              >
                <div className={`text-2xl font-bold ${card.color}`}>{card.value}</div>
                <div className="text-sm text-slate-500">{card.label}</div>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <i className="fa-solid fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input
              type="text"
              placeholder="Search by RFP, payee, job order..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all outline-none"
            />
          </div>
          {stageFilter !== "all" && (
            <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
              <span>
                Filtered by <span className="font-semibold text-slate-900">{stageFilterLabels[stageFilter] || stageFilter}</span>
              </span>
              <button
                type="button"
                onClick={() => setStageFilter("all")}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-200"
              >
                <i className="fa-solid fa-xmark" />
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <i className="fa-solid fa-circle-exclamation mr-2"></i>
            {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <i className="fa-solid fa-spinner fa-spin text-2xl text-blue-600"></i>
          </div>
        )}

        {/* Request List */}
        {!loading && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Payee</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Corporate Client</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Job Order</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Amount</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Stage</th>
                      <th className="text-right px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedGroupedRequests.map((row) => {
                      const rowMeta = stageMeta[row.cost_analysis_stage] || stageMeta.pending_submission;
                      return (
                        <tr key={row.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-6 py-4 text-slate-600">{row.jo_payee || "—"}</td>
                          <td className="px-6 py-4 text-slate-600">
                            {row.shipment_details?.corporate_client || "—"}
                          </td>
                          <td className="px-6 py-4 text-slate-600">
                            <div className="flex flex-col gap-1">
                              <span>{row.job_order || "—"}</span>
                              {row.jo_creator_name && (
                                <span className="text-xs text-slate-400">by {row.jo_creator_name}</span>
                              )}
                              {(row.jo_request_count || 0) > 1 && (
                                <span className="inline-flex w-fit rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                                  {row.jo_request_count} requests
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-slate-900 font-medium">
                            {row.jo_total_amount_usd > 0 ? (
                              <div className="flex flex-col">
                                <span>${money(row.jo_total_amount_usd)}</span>
                                <span className="text-xs text-slate-500">₱{money(row.jo_total_amount || row.total_amount)}</span>
                              </div>
                            ) : (
                              <span>₱{money(row.jo_total_amount || row.total_amount)}</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${rowMeta.tone}`}>
                              {rowMeta.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => openModal(row)}
                                className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <i className="fa-solid fa-eye mr-1"></i>
                                View
                              </button>
                              {row.cost_analysis && (
                                <button
                                  onClick={() => openAttachmentModal(row)}
                                  className="px-3 py-1.5 text-sm font-medium text-violet-600 hover:text-violet-700 hover:bg-violet-50 rounded-lg transition-colors"
                                >
                                  <i className="fa-solid fa-paperclip mr-1"></i>
                                  Upload
                                </button>
                              )}
                              {row.cost_analysis_report && (
                                <button
                                  onClick={() => downloadReport(row)}
                                  disabled={downloadLoadingId === row.id}
                                  className="px-3 py-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  {downloadLoadingId === row.id ? (
                                    <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                                  ) : (
                                    <i className="fa-solid fa-download mr-1"></i>
                                  )}
                                  PDF
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {paginatedGroupedRequests.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                          No approved requests found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col gap-4 border-t border-slate-100 px-6 py-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center">
                  <div>
                    {groupedRequests.length
                      ? `Showing ${pageStart}-${pageEnd} of ${groupedRequests.length} job orders`
                      : "No job orders to display"}
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="cost-analysis-page-size" className="text-slate-500">
                      Rows per page
                    </label>
                    <select
                      id="cost-analysis-page-size"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value) || 20);
                        setCurrentPage(1);
                      }}
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    >
                      {[10, 20, 50, 100].map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-start lg:self-auto">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={safeCurrentPage === 1}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    title="First page"
                  >
                    <i className="fa-solid fa-angles-left"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={safeCurrentPage === 1}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Previous page"
                  >
                    <i className="fa-solid fa-angle-left"></i>
                  </button>
                  <div className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700">
                    Page {safeCurrentPage} of {totalPages}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={safeCurrentPage === totalPages}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Next page"
                  >
                    <i className="fa-solid fa-angle-right"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safeCurrentPage === totalPages}
                    className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    title="Last page"
                  >
                    <i className="fa-solid fa-angles-right"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Cost analysis reports</h2>
                  <p className="text-sm text-slate-600">
                    Reports that have already been created. Use view, edit, or download.
                  </p>
                </div>
                <div className="text-sm text-slate-500">
                  {costAnalysisReportRows.length} report{costAnalysisReportRows.length === 1 ? "" : "s"}
                </div>
              </div>

              {costAnalysisReportRows.length ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-100">
                          <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Payee</th>
                          <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Job Order</th>
                          <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Stage</th>
                          <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Report Date</th>
                          <th className="text-left px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Updated</th>
                          <th className="text-right px-6 py-4 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {paginatedCostAnalysisReportRows.map((row) => {
                          const rowMeta = stageMeta[row.cost_analysis_stage] || stageMeta.pending_submission;
                          const report = row.cost_analysis_report || {};
                          return (
                            <tr key={`cost-report-${row.id}`} className="hover:bg-slate-50 transition-colors">
                              <td className="px-6 py-4 text-slate-700">{row.jo_payee || "—"}</td>
                              <td className="px-6 py-4 text-slate-600">
                                <div className="flex flex-col gap-1">
                                  <span>{row.job_order || "—"}</span>
                                  {row.jo_creator_name && (
                                    <span className="text-xs text-slate-400">by {row.jo_creator_name}</span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${rowMeta.tone}`}>
                                  {rowMeta.label}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-slate-600">
                                {report.report_date_created
                                  ? new Date(report.report_date_created).toLocaleDateString()
                                  : "—"}
                              </td>
                              <td className="px-6 py-4 text-sm text-slate-600">
                                {report.updated_at ? formatDateTime(report.updated_at) : formatDateTime(row.updated_at)}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="inline-flex items-center gap-3">
                                  <button
                                    type="button"
                                    onClick={() => openModal(row)}
                                    className="text-xs font-semibold text-blue-700 hover:underline"
                                  >
                                    View
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => openReportEditor(row)}
                                    className="text-xs font-semibold text-emerald-700 hover:underline"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => downloadReport(row)}
                                    disabled={downloadLoadingId === row.id}
                                    className="text-xs font-semibold text-slate-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                                  >
                                    {downloadLoadingId === row.id ? "Generating..." : "Download"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex flex-col gap-4 border-t border-slate-100 px-6 py-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-col gap-3 text-sm text-slate-600 sm:flex-row sm:items-center">
                      <div>
                        {costAnalysisReportRows.length
                          ? `Showing ${reportPageStart}-${reportPageEnd} of ${costAnalysisReportRows.length} reports`
                          : "No reports to display"}
                      </div>
                      <div className="flex items-center gap-2">
                        <label htmlFor="cost-analysis-report-page-size" className="text-slate-500">
                          Rows per page
                        </label>
                        <select
                          id="cost-analysis-report-page-size"
                          value={reportPageSize}
                          onChange={(e) => {
                            setReportPageSize(Number(e.target.value) || 10);
                            setReportCurrentPage(1);
                          }}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        >
                          {[5, 10, 20, 50].map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-start lg:self-auto">
                      <button
                        type="button"
                        onClick={() => setReportCurrentPage(1)}
                        disabled={safeReportCurrentPage === 1}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="First page"
                      >
                        <i className="fa-solid fa-angles-left"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => setReportCurrentPage((page) => Math.max(1, page - 1))}
                        disabled={safeReportCurrentPage === 1}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Previous page"
                      >
                        <i className="fa-solid fa-angle-left"></i>
                      </button>
                      <div className="rounded-xl bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700">
                        Page {safeReportCurrentPage} of {reportTotalPages}
                      </div>
                      <button
                        type="button"
                        onClick={() => setReportCurrentPage((page) => Math.min(reportTotalPages, page + 1))}
                        disabled={safeReportCurrentPage === reportTotalPages}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Next page"
                      >
                        <i className="fa-solid fa-angle-right"></i>
                      </button>
                      <button
                        type="button"
                        onClick={() => setReportCurrentPage(reportTotalPages)}
                        disabled={safeReportCurrentPage === reportTotalPages}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                        title="Last page"
                      >
                        <i className="fa-solid fa-angles-right"></i>
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="px-6 py-12 text-center text-slate-500">
                  No cost analysis reports created yet.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/60 backdrop-blur-md p-4">
            <div className="w-full max-w-6xl my-8 overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-[0_30px_90px_rgba(15,23,42,0.28)]">
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-slate-800/20 bg-slate-900 px-6 py-5 text-white">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">
                    Operational Review
                  </p>
                  <h2 className="mt-1 text-xl font-bold text-white">Cost Analysis</h2>
                  <p className="text-sm text-slate-300">{modalData?.job_order || activeJobOrder || "—"}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/10 text-white transition-colors hover:bg-white/20"
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </div>

              {/* Modal Content */}
              <div className="max-h-[calc(100vh-200px)] overflow-y-auto bg-slate-100 p-6">
                {modalMode === "preview" ? (
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            PDF Preview
                          </div>
                          <div className="mt-1 text-sm text-slate-600">
                            Review the JO-level cost analysis layout before switching into edit mode.
                          </div>
                        </div>
                        <span className={`inline-flex w-fit items-center px-4 py-2 rounded-full text-sm font-medium ${currentMeta.tone}`}>
                          {currentMeta.label}
                        </span>
                      </div>
                    </div>

                    <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm">
                      <div className="border-b border-slate-100 px-4 py-3">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                              Preview Docs
                            </div>
                            <div className="mt-1 text-sm text-slate-600">
                              View the generated documents for this Job Order.
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {[
                              { key: "requests", label: "Requests" },
                              { key: "liquidations", label: "Liquidations" },
                              { key: "vouchers", label: "Vouchers" },
                              { key: "cost", label: "Cost Analysis" },
                            ].map((tab) => (
                              <button
                                key={tab.key}
                                type="button"
                                onClick={() => setPreviewDocType(tab.key)}
                                className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                                  previewDocType === tab.key
                                    ? "bg-blue text-white shadow-sm"
                                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                }`}
                              >
                                {tab.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      {joLedgerLoading ? (
                        <div className="flex h-[220px] items-center justify-center text-sm text-slate-500">
                          Loading JO details...
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {joLedgerError ? (
                            <div className="px-4 py-3 text-sm text-red-600">{joLedgerError}</div>
                          ) : null}
                          <div className="flex items-center justify-between gap-3 px-4">
                            <div className="flex min-w-0 flex-wrap gap-2">
                              {(previewDocType === "requests"
                                ? joRequests
                                : previewDocType === "liquidations"
                                  ? joLiquidations
                                  : previewDocType === "vouchers"
                                    ? joVouchers
                                    : selectedCostRequestDoc
                                      ? [selectedCostRequestDoc]
                                      : []
                              ).map((doc, index) => {
                              const isActive =
                                (previewDocType === "requests" && index === previewSelection.requests) ||
                                (previewDocType === "liquidations" && index === previewSelection.liquidations) ||
                                (previewDocType === "vouchers" && index === previewSelection.vouchers) ||
                                (previewDocType === "cost" && index === previewSelection.cost);
                              const label =
                                previewDocType === "requests"
                                  ? `${doc.rfp_no || "Request"}`
                                  : previewDocType === "liquidations"
                                    ? `${doc.rfp_no || "Liquidation"}${doc.liquidation_report?.liquidation_no || doc.liquidation?.liquidation_no || doc.liquidation_no ? ` / ${doc.liquidation_report?.liquidation_no || doc.liquidation?.liquidation_no || doc.liquidation_no}` : ""}`
                                    : previewDocType === "vouchers"
                                      ? `${doc.voucher_no || "Voucher"}`
                                      : `${doc.rfp_no || "Cost Analysis"}`;
                                return (
                                  <button
                                    key={`${previewDocType}-${doc.id || index}`}
                                    type="button"
                                    onClick={() =>
                                      setPreviewSelection((prev) => ({
                                        ...prev,
                                        [previewDocType]: index,
                                      }))
                                    }
                                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                                      isActive
                                        ? "bg-slate-900 text-white"
                                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                    }`}
                                  >
                                    {label}
                                  </button>
                                );
                              })}
                            </div>
                            <div className="inline-flex shrink-0 items-center overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                              <button
                                type="button"
                                aria-label="Zoom out"
                                title="Zoom out"
                                onClick={() => setPreviewZoom((value) => Math.max(0.8, Number((value - 0.1).toFixed(1))))}
                                disabled={previewZoom <= 0.8}
                                className="px-2.5 py-1.5 text-lg font-semibold leading-none text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                −
                              </button>
                              <span className="min-w-[3.5rem] border-x border-slate-200 px-2 py-1.5 text-center text-xs font-semibold text-slate-600">
                                {Math.round(previewZoom * 100)}%
                              </span>
                              <button
                                type="button"
                                aria-label="Zoom in"
                                title="Zoom in"
                                onClick={() => setPreviewZoom((value) => Math.min(2, Number((value + 0.1).toFixed(1))))}
                                disabled={previewZoom >= 2}
                                className="px-2.5 py-1.5 text-lg font-semibold leading-none text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                +
                              </button>
                            </div>
                          </div>
                          <div
                            className="origin-top"
                            style={{
                              width: `${100 / previewZoom}%`,
                              height: `${previewIframeHeight * previewZoom}px`,
                              margin: "0 auto",
                            }}
                          >
                            <iframe
                              title="Preview docs"
                              srcDoc={
                                previewDocType === "requests"
                                  ? requestPreviewHtml
                                  : previewDocType === "liquidations"
                                    ? liquidationPreviewHtml
                                    : previewDocType === "vouchers"
                                      ? voucherPreviewHtml
                                      : costAnalysisPreviewHtml
                              }
                              onLoad={(event) => {
                                const doc = event.currentTarget.contentDocument;
                                const height = doc?.documentElement?.scrollHeight;
                                if (height) setPreviewIframeHeight(Math.min(Math.max(height + 24, 320), 4000));
                              }}
                              style={{
                                height: `${previewIframeHeight}px`,
                                transform: `scale(${previewZoom})`,
                                transformOrigin: "top center",
                              }}
                              className="w-full border-0 bg-white"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                {modalMode === "editor" && detailLoading && (
                  <div className="flex items-center justify-center py-12">
                    <i className="fa-solid fa-spinner fa-spin text-2xl text-blue-600"></i>
                  </div>
                )}

                {modalMode === "editor" && !detailLoading && modalData && (
                  <div className="space-y-6">
                    {/* Status Banner */}
                    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                        <span className={`inline-flex w-fit items-center px-4 py-2 rounded-full text-sm font-medium ${currentMeta.tone}`}>
                          {currentMeta.label}
                        </span>
                        <span className="text-sm text-slate-600">{currentMeta.description}</span>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="relative h-2 overflow-hidden rounded-full bg-slate-200">
                      <div
                        className="absolute left-0 top-0 h-full bg-gradient-to-r from-sky-500 via-blue-500 to-emerald-500 transition-all duration-500"
                        style={{ width: progressWidth }}
                      ></div>
                    </div>

                    {/* Connected Requests Section - Only approved requests */}
                    {(() => {
                      const approvedRequests = joRequests.filter((req) => req.status === "approved");
                      if (!approvedRequests.length) return null;
                      return (
                        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                          <div className="flex items-center justify-between bg-slate-800 px-4 py-3 text-white">
                            <h3 className="font-semibold">
                              <i className="fa-solid fa-file-invoice mr-2"></i>
                              Connected Requests ({approvedRequests.length})
                            </h3>
                            <span className="text-xs text-slate-300">Approved requests for this J.O.</span>
                          </div>
                          <div className="p-4 max-h-[300px] overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-xs uppercase tracking-[0.08em] text-slate-500 border-b border-slate-200">
                                  <th className="pb-2 font-semibold">RFP No.</th>
                                  <th className="pb-2 font-semibold">Payee</th>
                                  <th className="pb-2 font-semibold">Particulars</th>
                                  <th className="pb-2 font-semibold">Fixed Charges</th>
                                  <th className="pb-2 text-center font-semibold">For Liquidation</th>
                                  <th className="pb-2 text-center font-semibold">Tax</th>
                                  <th className="pb-2 text-center font-semibold">Currency</th>
                                  <th className="pb-2 text-right font-semibold">Total Amount</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                {approvedRequests.map((req) => {
                                  const reqCurrency = String(req.currency || "PHP").toUpperCase();
                                  const reqExchangeRate = Number(req.exchange_rate || 1);
                                  const isReqUsd = reqCurrency === "USD" && reqExchangeRate > 0;
                                  const totalPhp = Number(req.total_amount || 0);
                                  const totalUsd = isReqUsd ? totalPhp / reqExchangeRate : 0;
                                  const particularsText = (req.particulars || [])
                                    .map((p) => p.description || "")
                                    .filter(Boolean)
                                    .join(", ") || "—";
                                  const fixedChargeParticulars = (req.particulars || []).filter((particular) =>
                                    isFixedChargeParticular(particular)
                                  );
                                  const fixedChargesText =
                                    fixedChargeParticulars
                                      .map((particular) => particular.description || "")
                                      .filter(Boolean)
                                      .join(", ") || "—";
                                  const hasFixedCharges = fixedChargeParticulars.length > 0;
                                  // Check if any particular has tax
                                  const hasTax = (req.particulars || []).some((p) => p.with_tax);
                                  const isForLiquidation = Boolean(req.for_liquidation);
                                  return (
                                    <tr key={req.id} className="hover:bg-slate-50">
                                      <td className="py-2 pr-2">
                                        <span className="font-medium text-blue-700">{req.rfp_no || "—"}</span>
                                      </td>
                                      <td className="py-2 pr-2 text-slate-700">{req.payee || "—"}</td>
                                      <td className="py-2 pr-2 text-slate-600 max-w-[200px] truncate" title={particularsText}>
                                        {particularsText}
                                      </td>
                                      <td className="py-2 pr-2">
                                        <div className="max-w-[240px]" title={fixedChargesText}>
                                          {hasFixedCharges ? (
                                            <>
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                                Yes
                                              </span>
                                              <div className="mt-1 truncate text-xs text-slate-600">{fixedChargesText}</div>
                                            </>
                                          ) : (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                              None
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="py-2 px-2 text-center">
                                        {isForLiquidation ? (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            Yes
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                            No
                                          </span>
                                        )}
                                      </td>
                                      <td className="py-2 px-2 text-center">
                                        {hasTax ? (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                            With Tax
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                            No Tax
                                          </span>
                                        )}
                                      </td>
                                      <td className="py-2 px-2 text-center">
                                        {isReqUsd ? (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            USD
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                                            PHP
                                          </span>
                                        )}
                                      </td>
                                      <td className="py-2 pl-2 text-right font-semibold">
                                        {isReqUsd ? (
                                          <div>
                                            <div className="text-green-700">${money(totalUsd)}</div>
                                            <div className="text-xs text-slate-500">₱{money(totalPhp)}</div>
                                          </div>
                                        ) : (
                                          <div className="text-slate-900">₱{money(totalPhp)}</div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                              <tfoot>
                                <tr className="bg-slate-50 border-t border-slate-200">
                                  <td colSpan={7} className="py-2 px-2 text-right font-semibold text-slate-700">
                                    Total (PHP)
                                  </td>
                                  <td className="py-2 pl-2 text-right font-bold text-slate-900">
                                    ₱{money(approvedRequests.reduce((sum, req) => sum + Number(req.total_amount || 0), 0))}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Attached Documents Section */}
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                      <div className="flex items-center justify-between bg-slate-800 px-4 py-3 text-white">
                        <h3 className="font-semibold">
                          <i className="fa-solid fa-paperclip mr-2"></i>
                          Attached Documents
                        </h3>
                        <span className="text-xs text-slate-300">
                          Check the documents to include in the downloaded report ({selectedAttachedDocuments.length} selected)
                        </span>
                      </div>
                      <div className="p-4 max-h-[300px] overflow-y-auto space-y-6">
                        {/* Track N Trace sub-section */}
                        <div>
                          <div className="mb-2 flex items-center gap-2">
                            <i className="fa-solid fa-truck-fast text-blue-600"></i>
                            <h4 className="text-sm font-semibold text-slate-700">Track N Trace</h4>
                            <span className="text-xs text-slate-400">({shipmentDocuments.length})</span>
                          </div>
                          {shipmentDocumentsLoading ? (
                            <div className="flex items-center justify-center py-6 text-sm text-slate-500">
                              <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                              Loading documents...
                            </div>
                          ) : shipmentDocuments.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-sm text-slate-400">
                              No documents uploaded for this shipment yet.
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {shipmentDocuments.map((doc) => {
                                const docKey = `shipment-doc-${doc.id}`;
                                const isChecked = selectedAttachmentKeys.has(docKey);
                                return (
                                  <label
                                    key={doc.id}
                                    className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 hover:bg-slate-100 transition-colors cursor-pointer"
                                  >
                                    <div className="flex min-w-0 items-center gap-3">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggleAttachmentSelected(docKey)}
                                        className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                                      />
                                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100">
                                        <i className={`fa-solid ${fileIconClass(doc.document_name)} ${fileIconColor(doc.document_name)}`}></i>
                                      </div>
                                      <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-slate-900">
                                          {doc.document_title || doc.document_name || "Untitled document"}
                                        </p>
                                        <p className="truncate text-xs text-slate-500">
                                          {doc.document_name || "—"} • {formatDateTime(doc.uploaded_at)}
                                        </p>
                                      </div>
                                    </div>
                                    <a
                                      href={doc.document_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                                      title="View"
                                    >
                                      <i className="fa-solid fa-external-link-alt"></i>
                                    </a>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Accounting sub-section */}
                        <div>
                          <div className="mb-2 flex items-center gap-2">
                            <i className="fa-solid fa-calculator text-emerald-600"></i>
                            <h4 className="text-sm font-semibold text-slate-700">Accounting</h4>
                            <span className="text-xs text-slate-400">
                              ({joRequests.length} RFP{joRequests.length === 1 ? "" : "s"}, {joLiquidations.length} liquidation
                              {joLiquidations.length === 1 ? "" : "s"}, {joVouchers.length} voucher{joVouchers.length === 1 ? "" : "s"})
                            </span>
                          </div>
                          {!joRequests.length && !joLiquidations.length && !joVouchers.length ? (
                            <div className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-sm text-slate-400">
                              No RFP, liquidation, or voucher records found for this J.O.
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {joRequests.map((req, index) => {
                                const docKey = `rfp-${req.id || index}`;
                                const isChecked = selectedAttachmentKeys.has(docKey);
                                return (
                                  <div
                                    key={docKey}
                                    className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 hover:bg-slate-100 transition-colors"
                                  >
                                    <label className="flex min-w-0 flex-1 items-center gap-3 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggleAttachmentSelected(docKey)}
                                        className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                                      />
                                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100">
                                        <i className="fa-solid fa-file-invoice-dollar text-amber-600"></i>
                                      </div>
                                      <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-slate-900">
                                          RFP {req.rfp_no || "—"}
                                        </p>
                                        <p className="truncate text-xs text-slate-500">
                                          {req.payee || "—"} • {formatDateTime(req.created_at)}
                                        </p>
                                      </div>
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() => openAccountingDocPreview("requests", index)}
                                      className="shrink-0 text-xs font-semibold text-blue-600 hover:underline"
                                    >
                                      View <i className="fa-solid fa-external-link-alt ml-1"></i>
                                    </button>
                                  </div>
                                );
                              })}
                              {joLiquidations.map((req, index) => {
                                const docKey = `liq-${req.id || index}`;
                                const isChecked = selectedAttachmentKeys.has(docKey);
                                return (
                                  <div
                                    key={docKey}
                                    className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 hover:bg-slate-100 transition-colors"
                                  >
                                    <label className="flex min-w-0 flex-1 items-center gap-3 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggleAttachmentSelected(docKey)}
                                        className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                                      />
                                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-purple-100">
                                        <i className="fa-solid fa-receipt text-purple-600"></i>
                                      </div>
                                      <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-slate-900">
                                          Liquidation{" "}
                                          {req.liquidation_report?.liquidation_no || req.liquidation?.liquidation_no || req.rfp_no || "—"}
                                        </p>
                                        <p className="truncate text-xs text-slate-500">
                                          {req.payee || "—"} • {formatDateTime(req.liquidation_report?.created_at || req.liquidation?.created_at)}
                                        </p>
                                      </div>
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() => openAccountingDocPreview("liquidations", index)}
                                      className="shrink-0 text-xs font-semibold text-blue-600 hover:underline"
                                    >
                                      View <i className="fa-solid fa-external-link-alt ml-1"></i>
                                    </button>
                                  </div>
                                );
                              })}
                              {joVouchers.map((voucher, index) => {
                                const docKey = `voucher-${voucher.id || index}`;
                                const isChecked = selectedAttachmentKeys.has(docKey);
                                return (
                                  <div
                                    key={docKey}
                                    className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 hover:bg-slate-100 transition-colors"
                                  >
                                    <label className="flex min-w-0 flex-1 items-center gap-3 cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggleAttachmentSelected(docKey)}
                                        className="h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                                      />
                                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-green-100">
                                        <i className="fa-solid fa-money-check-dollar text-green-600"></i>
                                      </div>
                                      <div className="min-w-0">
                                        <p className="truncate text-sm font-medium text-slate-900">
                                          Voucher {voucher.voucher_no || "—"}
                                        </p>
                                        <p className="truncate text-xs text-slate-500">
                                          {voucher.payee || "—"} • {formatDateTime(voucher.check_date || voucher.created_at)}
                                        </p>
                                      </div>
                                    </label>
                                    <button
                                      type="button"
                                      onClick={() => openAccountingDocPreview("vouchers", index)}
                                      className="shrink-0 text-xs font-semibold text-blue-600 hover:underline"
                                    >
                                      View <i className="fa-solid fa-external-link-alt ml-1"></i>
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Request Info */}
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                      {/* Editable Payee Field (rule of payee applied as default) */}
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                          {joPayeeLabel}
                        </label>
                        <input
                          type="text"
                          value={reportPayee}
                          onChange={(e) => setReportPayee(e.target.value)}
                          disabled={!canEdit}
                          placeholder={`Enter ${joPayeeLabel.toLowerCase()}`}
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100"
                        />
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <label className="mb-1 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                          Bill To
                        </label>
                        <label className="mb-3 flex items-center gap-2 text-xs font-medium text-slate-600">
                          <input
                            type="checkbox"
                            checked={billToSameAsConsignee}
                            onChange={(e) => setBillToSameAsConsignee(e.target.checked)}
                            disabled={!canEdit}
                            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                          />
                          Same as {joPayeeLabel.toLowerCase()}
                        </label>
                        <input
                          type="text"
                          value={billToSameAsConsignee ? reportPayee : reportBillTo}
                          onChange={(e) => setReportBillTo(e.target.value)}
                          disabled={!canEdit || billToSameAsConsignee}
                          placeholder="Enter billing party"
                          className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-100"
                        />
                      </div>
                      {/* Read-only fields */}
                      {[
                        { label: "J.O. No.", value: modalData?.job_order || "—" },
                        { label: "HBL/HAWB", value: shipmentDetails?.tracking_no || "—" },
                        { label: "Shipment", value: shipmentDetails?.shipment_description || "—" },
                      ].map((item) => (
                        <div key={item.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                          <div className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                            {item.label}
                          </div>
                          <div className="text-sm font-semibold text-slate-900 break-words">
                            {item.value}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Manual Header Fields */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <label className="mb-2 block text-sm font-semibold text-slate-700">Date</label>
                        <input
                          type="date"
                          value={reportDateCreated}
                          onChange={(e) => setReportDateCreated(e.target.value)}
                          disabled={!canEdit}
                          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-900 shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                        />
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <label className="mb-2 block text-sm font-semibold text-slate-700">Dock</label>
                        <input
                          type="text"
                          value={reportDock}
                          onChange={(e) => setReportDock(e.target.value)}
                          disabled={!canEdit}
                          placeholder="Enter dock"
                          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-900 shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                        />
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <label className="mb-2 block text-sm font-semibold text-slate-700">P.O. No.</label>
                        <input
                          type="text"
                          value={reportPoNo}
                          onChange={(e) => setReportPoNo(e.target.value)}
                          disabled={!canEdit}
                          placeholder="Enter P.O. number"
                          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-900 shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                        />
                      </div>
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <label className="mb-2 block text-sm font-semibold text-slate-700">Reference No.</label>
                        <input
                          type="text"
                          value={reportReferenceNo}
                          onChange={(e) => setReportReferenceNo(e.target.value)}
                          disabled={!canEdit}
                          placeholder="Enter reference number"
                          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-900 shadow-sm outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-100"
                        />
                      </div>
                    </div>

                    {/* Billing Charges Section */}
                    <div ref={reportSectionRef} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <div className="flex items-center justify-between bg-slate-900 px-4 py-3 text-white">
                        <h3 className="font-semibold">
                          <i className="fa-solid fa-file-invoice-dollar mr-2"></i>
                          Billing Charges
                        </h3>
                        {canEdit && (
                          <button
                            onClick={addBillingChargeRow}
                            className="rounded-lg border border-white/15 bg-white/10 px-3 py-1 text-sm transition-colors hover:bg-white/20"
                          >
                            <i className="fa-solid fa-plus mr-1"></i>
                            Add Row
                          </button>
                        )}
                      </div>
                      <div className="p-5">
                        <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Billing Charges
                          </div>
                          <div className="mt-1 text-sm text-slate-600">
                            Add billing line items here and the subtotal updates automatically.
                          </div>
                        </div>
                        <table className="w-full">
                          <thead>
                            <tr className="text-left text-xs uppercase tracking-[0.08em] text-slate-500">
                              <th className="pb-2 font-semibold">Particulars</th>
                              <th className="pb-2 text-center font-semibold">Output VAT (12%)</th>
                              <th className="pb-2 text-center font-semibold">EWT</th>
                              <th className="pb-2 text-center font-semibold">Currency</th>
                              <th className="pb-2 text-right font-semibold">Rate</th>
                              <th className="pb-2 text-right font-semibold">{billingAmountColumnLabel}</th>
                              <th className="pb-2 text-right font-semibold">Computed</th>
                              {canEdit && <th className="pb-2"></th>}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {billingChargeRows.map((row, index) => (
                              <tr key={row.id}>
                                <td className="py-2 pr-2">
                                  <input
                                    type="text"
                                    value={row.particulars}
                                    onChange={(e) => updateBillingChargeRow(index, "particulars", e.target.value)}
                                    disabled={!canEdit}
                                    placeholder="Type or pick particular"
                                    list="billing-charge-particulars-options"
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm disabled:bg-slate-100"
                                  />
                                </td>
                                <td className="py-2 px-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={row.has_output_vat}
                                    onChange={(e) => updateBillingChargeRow(index, "has_output_vat", e.target.checked)}
                                    disabled={!canEdit}
                                    className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <div className="flex items-center gap-2 justify-center">
                                    <input
                                      type="checkbox"
                                      checked={row.has_ewt}
                                      onChange={(e) => updateBillingChargeRow(index, "has_ewt", e.target.checked)}
                                      disabled={!canEdit}
                                      className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    {row.has_ewt && (
                                      <select
                                        value={row.ewt_rate}
                                        onChange={(e) => updateBillingChargeRow(index, "ewt_rate", e.target.value)}
                                        disabled={!canEdit}
                                        className="px-2 py-1 rounded-lg border border-slate-200 text-sm disabled:bg-slate-100"
                                      >
                                        <option value="">%</option>
                                        <option value="1">1%</option>
                                        <option value="2">2%</option>
                                        <option value="5">5%</option>
                                      </select>
                                    )}
                                  </div>
                                </td>
                                <td className="py-2 px-2">
                                  <select
                                    value={row.currency || "PHP"}
                                    onChange={(e) => updateBillingChargeRow(index, "currency", e.target.value)}
                                    disabled={!canEdit}
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm disabled:bg-slate-100"
                                  >
                                    <option value="PHP">PHP</option>
                                    <option value="USD">USD</option>
                                  </select>
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="number"
                                    step="0.0001"
                                    value={row.exchange_rate ?? 1}
                                    onChange={(e) => updateBillingChargeRow(index, "exchange_rate", e.target.value)}
                                    disabled={!canEdit || String(row.currency || "PHP").toUpperCase() === "PHP"}
                                    placeholder="1.0000"
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-right text-sm disabled:bg-slate-100"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="number"
                                    value={row.base_amount}
                                    onChange={(e) => updateBillingChargeRow(index, "base_amount", e.target.value)}
                                    disabled={!canEdit}
                                    placeholder="0.00"
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-right text-sm disabled:bg-slate-100"
                                  />
                                </td>
                                <td className="py-2 pl-2 text-right font-medium text-slate-900">
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={row.computed_amount}
                                    onChange={(e) => updateBillingChargeRow(index, "computed_amount", e.target.value)}
                                    disabled={!canEdit}
                                    placeholder="0.00"
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-right text-sm font-medium disabled:bg-slate-100"
                                  />
                                </td>
                                {canEdit && (
                                  <td className="py-2 pl-2">
                                    <button
                                      onClick={() => removeBillingChargeRow(index)}
                                      disabled={billingChargeRows.length === 1}
                                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30"
                                    >
                                      <i className="fa-solid fa-trash-can"></i>
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            {billingChargeBreakdown.hasVat && (
                              <>
                                <tr className="bg-emerald-50/70">
                                  <td colSpan={canEdit ? 6 : 5} className="py-2 px-4 text-right text-sm font-semibold text-slate-700">
                                    Total Cost / VAT Exclusive
                                  </td>
                                  <td className="py-2 px-4 text-right text-sm font-bold text-slate-700">
                                    {money(billingChargeBreakdown.vatExclusive)}
                                  </td>
                                  {canEdit && <td></td>}
                                </tr>
                                <tr className="bg-emerald-50/70">
                                  <td colSpan={canEdit ? 6 : 5} className="py-2 px-4 text-right text-sm font-semibold text-slate-700">
                                    VAT 12%
                                  </td>
                                  <td className="py-2 px-4 text-right text-sm font-bold text-slate-700">
                                    {money(billingChargeBreakdown.vat)}
                                  </td>
                                  {canEdit && <td></td>}
                                </tr>
                              </>
                            )}
                            {Object.entries(billingChargeBreakdown.ewtByRate).map(([rate, amount]) => (
                              <tr key={`ewt-${rate}`} className="bg-emerald-50/70">
                                <td colSpan={canEdit ? 6 : 5} className="py-2 px-4 text-right text-sm font-semibold text-slate-700">
                                  EWT {rate}%
                                </td>
                                <td className="py-2 px-4 text-right text-sm font-bold text-slate-700">
                                  {money(amount)}
                                </td>
                                {canEdit && <td></td>}
                              </tr>
                            ))}
                            <tr className="bg-emerald-50">
                              <td colSpan={canEdit ? 6 : 5} className="py-3 px-4 text-right font-semibold text-slate-700">
                                Subtotal
                              </td>
                              <td className="py-3 px-4 text-right font-bold text-emerald-700">
                                {money(billingChargesSubtotal)}
                              </td>
                              {canEdit && <td></td>}
                            </tr>
                          </tfoot>
                        </table>
                        <datalist id="billing-charge-particulars-options">
                          {particularsOptions.map((p) => (
                            <option key={p.id} value={p.name} />
                          ))}
                        </datalist>
                      </div>
                    </div>

                    {/* Receipted Charges Section */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <div className="flex items-center justify-between bg-slate-900 px-4 py-3 text-white">
                        <h3 className="font-semibold">
                          <i className="fa-solid fa-receipt mr-2"></i>
                          Receipted Charges
                        </h3>
                        {canEdit && (
                          <button
                            onClick={addReceiptedChargeRow}
                            className="rounded-lg border border-white/15 bg-white/10 px-3 py-1 text-sm transition-colors hover:bg-white/20"
                          >
                            <i className="fa-solid fa-plus mr-1"></i>
                            Add Row
                          </button>
                        )}
                      </div>
                      <div className="p-5">
                        {canEdit ? (
                          <div className="mb-5 grid gap-4 xl:grid-cols-2">
                            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                              <div className="mb-3 flex items-start justify-between gap-3">
                                <div>
                                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                                    Request Particular Sources
                                  </div>
                                  <div className="mt-1 text-sm text-slate-600">
                                    Add any approved request particular from this J.O.
                                  </div>
                                </div>
                                <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                                  {joRequestParticularSourceRows.length} item(s)
                                </div>
                              </div>
                              <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
                                {joRequestParticularGroups.length ? (
                                  joRequestParticularGroups.map((group) => (
                                    <details
                                      key={group.key}
                                      className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                                    >
                                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
                                        <div className="min-w-0">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <div className="text-sm font-semibold text-slate-800">{group.rfpNo}</div>
                                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                              {group.rowCount} item{group.rowCount === 1 ? "" : "s"}
                                            </span>
                                          </div>
                                          {group.payee ? (
                                            <div className="mt-1 truncate text-xs text-slate-500">{group.payee}</div>
                                          ) : null}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="text-right">
                                            <div className="text-xs uppercase tracking-[0.12em] text-slate-400">Total</div>
                                            <div className="text-sm font-semibold text-slate-700">{money(group.totalAmount)}</div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              addReceiptedRequestGroup(group);
                                            }}
                                            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                                          >
                                            Add All
                                          </button>
                                        </div>
                                      </summary>
                                      <div className="space-y-2 border-t border-slate-100 bg-slate-50/70 p-3">
                                        {group.rows.map((row) => (
                                          <div key={row.id} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-100">
                                            <div className="min-w-0">
                                              <div className="truncate text-sm font-medium text-slate-800">{row.particulars || "—"}</div>
                                              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                                <span>From request particulars</span>
                                                {isReceiptedSourceAdded(createReceiptedChargeRowFromRequest(row)) ? (
                                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700 ring-1 ring-emerald-200">
                                                    <i className="fa-solid fa-check text-[10px]"></i>
                                                    Added
                                                  </span>
                                                ) : null}
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <div className="text-sm font-semibold text-slate-700">{money(row.amount)}</div>
                                              {isReceiptedSourceAdded(createReceiptedChargeRowFromRequest(row)) ? (
                                                <button
                                                  type="button"
                                                  onClick={() => removeReceiptedSourceRows([createReceiptedChargeRowFromRequest(row)])}
                                                  className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
                                                >
                                                  Remove
                                                </button>
                                              ) : (
                                                <button
                                                  type="button"
                                                  onClick={() => appendReceiptedSourceRows([createReceiptedChargeRowFromRequest(row)])}
                                                  className="rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100"
                                                >
                                                  Add
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  ))
                                ) : (
                                  <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-6 text-sm text-slate-500">
                                    No request particulars available for this J.O.
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                              <div className="mb-3 flex items-start justify-between gap-3">
                                <div>
                                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                                    Liquidation Cost Sources
                                  </div>
                                  <div className="mt-1 text-sm text-slate-600">
                                    Add cost incurred rows from liquidations tied to this J.O.
                                  </div>
                                </div>
                                <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
                                  {joLiquidationSourceRows.length} item(s)
                                </div>
                              </div>
                              <div className="max-h-72 space-y-3 overflow-y-auto pr-1">
                                {joLiquidationSourceGroups.length ? (
                                  joLiquidationSourceGroups.map((group) => (
                                    <details
                                      key={group.key}
                                      className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                                    >
                                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
                                        <div className="min-w-0">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <div className="text-sm font-semibold text-slate-800">{group.liquidationNo}</div>
                                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                              {group.rowCount} item{group.rowCount === 1 ? "" : "s"}
                                            </span>
                                          </div>
                                          <div className="mt-1 truncate text-xs text-slate-500">
                                            {group.rfpNo}
                                            {group.liquidationStatus ? ` • ${group.liquidationStatus}` : ""}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="text-right">
                                            <div className="text-xs uppercase tracking-[0.12em] text-slate-400">Total</div>
                                            <div className="text-sm font-semibold text-slate-700">{money(group.totalAmount)}</div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              addReceiptedLiquidationGroup(group);
                                            }}
                                            className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
                                          >
                                            Add All
                                          </button>
                                        </div>
                                      </summary>
                                      <div className="space-y-2 border-t border-slate-100 bg-slate-50/70 p-3">
                                        {group.rows.map((row) => (
                                          <div key={row.id} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-100">
                                            <div className="min-w-0">
                                              <div className="truncate text-sm font-medium text-slate-800">{row.particulars || "—"}</div>
                                              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                                <span>O.R.: {row.or_no || "—"}</span>
                                                {isReceiptedSourceAdded(createReceiptedChargeRowFromLiquidation(row)) ? (
                                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700 ring-1 ring-emerald-200">
                                                    <i className="fa-solid fa-check text-[10px]"></i>
                                                    Added
                                                  </span>
                                                ) : null}
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <div className="text-sm font-semibold text-slate-700">{money(row.amount)}</div>
                                              {isReceiptedSourceAdded(createReceiptedChargeRowFromLiquidation(row)) ? (
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    removeReceiptedSourceRows([createReceiptedChargeRowFromLiquidation(row)])
                                                  }
                                                  className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
                                                >
                                                  Remove
                                                </button>
                                              ) : (
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    appendReceiptedSourceRows([createReceiptedChargeRowFromLiquidation(row)])
                                                  }
                                                  className="rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100"
                                                >
                                                  Add
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  ))
                                ) : (
                                  <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-6 text-sm text-slate-500">
                                    No liquidation cost rows available for this J.O.
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : null}

                        <table className="w-full">
                          <thead>
                            <tr className="text-left text-xs uppercase tracking-[0.08em] text-slate-500">
                              <th className="pb-2 font-semibold">Source</th>
                              <th className="pb-2 font-semibold">RFP No.</th>
                              <th className="pb-2 font-semibold">Liquidation</th>
                              <th className="pb-2 font-semibold">Particulars</th>
                              <th className="pb-2 font-semibold">O.R. No.</th>
                              <th className="pb-2 text-right font-semibold">Amount</th>
                              {canEdit && <th className="pb-2"></th>}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {receiptedChargeRows.map((row, index) => (
                              <tr key={row.id}>
                                <td className="py-2 pr-2 align-top">
                                  <span
                                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                      row.source_type === "liquidation_actual_cost"
                                        ? "bg-emerald-50 text-emerald-700"
                                        : row.source_type === "request_particular"
                                          ? "bg-blue-50 text-blue-700"
                                          : "bg-slate-100 text-slate-600"
                                    }`}
                                  >
                                    {row.source_type === "liquidation_actual_cost"
                                      ? "Liquidation"
                                      : row.source_type === "request_particular"
                                        ? "Request"
                                        : "Manual"}
                                  </span>
                                </td>
                                <td className="py-2 pr-2 text-sm text-slate-700 align-top">
                                  {row.source_rfp_no || "—"}
                                </td>
                                <td className="py-2 pr-2 text-sm text-slate-700 align-top">
                                  {row.liquidation_no || row.liquidation_id ? (
                                    <div className="space-y-1">
                                      <div className="font-medium text-slate-800">
                                        {row.liquidation_no || row.liquidation_id}
                                      </div>
                                      {row.liquidation_status ? (
                                        <div className="text-xs text-slate-500">{row.liquidation_status}</div>
                                      ) : null}
                                    </div>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                                <td className="py-2 pr-2">
                                  <input
                                    type="text"
                                    value={row.particulars}
                                    onChange={(e) => updateReceiptedChargeRow(index, "particulars", e.target.value)}
                                    disabled={!canEdit}
                                    placeholder="Enter particular"
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-100"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="text"
                                    value={row.or_no}
                                    onChange={(e) => updateReceiptedChargeRow(index, "or_no", e.target.value)}
                                    disabled={!canEdit}
                                    placeholder="Enter O.R. No."
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm disabled:bg-slate-100"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="number"
                                    value={row.amount}
                                    onChange={(e) => updateReceiptedChargeRow(index, "amount", e.target.value)}
                                    disabled={!canEdit}
                                    placeholder="0.00"
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-right text-sm disabled:bg-slate-100"
                                  />
                                </td>
                                {canEdit && (
                                  <td className="py-2 pl-2">
                                    <button
                                      onClick={() => removeReceiptedChargeRow(index)}
                                      disabled={receiptedChargeRows.length === 1}
                                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30"
                                    >
                                      <i className="fa-solid fa-trash-can"></i>
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-violet-50">
                              <td colSpan={5} className="py-3 px-4 text-right font-semibold text-slate-700">
                                Subtotal
                              </td>
                              <td className="py-3 px-4 text-right font-bold text-violet-700">
                                {money(receiptedChargesSubtotal)}
                              </td>
                              {canEdit && <td></td>}
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>

                    {/* Grand Total */}
                    <div
                      className="flex items-center justify-between rounded-2xl border border-blue-200 p-4 text-white shadow-sm"
                      style={{
                        background:
                          "linear-gradient(90deg, #1d4ed8 0%, #0f172a 100%)",
                      }}
                    >
                      <span className="font-semibold text-lg">
                        <i className="fa-solid fa-calculator mr-2"></i>
                        Grand Total (Billing + Receipted)
                      </span>
                      <span className="text-2xl font-bold">{money(grandTotal)}</span>
                    </div>

                    {/* Actual Cost Section */}
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <div className="flex items-center justify-between bg-slate-900 px-4 py-3 text-white">
                        <h3 className="font-semibold">
                          <i className="fa-solid fa-coins mr-2"></i>
                          Actual Cost
                        </h3>
                        {canEdit && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={seedActualFromFixedCharges}
                              disabled={!joFixedChargeActualRows.length}
                              className="rounded-lg border border-white/15 bg-white/10 px-3 py-1 text-sm transition-colors hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <i className="fa-solid fa-file-arrow-down mr-1"></i>
                              Get fixed charges
                            </button>
                            <button
                              onClick={addActualCostRow}
                              className="rounded-lg border border-white/15 bg-white/10 px-3 py-1 text-sm transition-colors hover:bg-white/20"
                            >
                              <i className="fa-solid fa-plus mr-1"></i>
                              Add Row
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="p-5">
                        {canEdit ? (
                          <div className="mb-5 grid gap-4 lg:grid-cols-2">
                            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                              <div className="mb-3">
                                <div className="text-sm font-semibold text-slate-900">Request Particular Sources</div>
                                <div className="text-xs text-slate-500">
                                  Add actual cost rows directly from approved request particulars in this J.O.
                                </div>
                              </div>
                              <div className="space-y-3">
                                {joRequestParticularGroups.length ? (
                                  joRequestParticularGroups.map((group) => (
                                    <details
                                      key={`actual-request-${group.key}`}
                                      className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                                    >
                                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
                                        <div className="min-w-0">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <div className="text-sm font-semibold text-slate-800">{group.rfpNo}</div>
                                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                              {group.rowCount} item{group.rowCount === 1 ? "" : "s"}
                                            </span>
                                            {group.fixedChargeCount ? (
                                              <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                                                {group.fixedChargeCount} fixed charge{group.fixedChargeCount === 1 ? "" : "s"}
                                              </span>
                                            ) : null}
                                          </div>
                                          <div className="mt-1 truncate text-xs text-slate-500">
                                            {group.payee || "—"}
                                            {group.fixedChargeCount ? " • Has fixed charges" : ""}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="text-right">
                                            <div className="text-xs uppercase tracking-[0.12em] text-slate-400">Total</div>
                                            <div className="text-sm font-semibold text-slate-700">{money(group.totalAmount)}</div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              addActualCostRequestGroup(group);
                                            }}
                                            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100"
                                          >
                                            Add All
                                          </button>
                                        </div>
                                      </summary>
                                      <div className="space-y-2 border-t border-slate-100 bg-slate-50/70 p-3">
                                        {group.rows.map((row) => (
                                          <div key={`actual-request-row-${row.id}`} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-100">
                                            <div className="min-w-0 flex-1">
                                              <div className="truncate text-sm font-medium text-slate-800">{row.particulars || "—"}</div>
                                              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                                <span>Amount: {money(row.amount)}</span>
                                                {isActualCostSourceAdded(createActualCostRowFromRequest(row)) ? (
                                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                                                    <i className="fa-solid fa-check text-[10px]"></i>
                                                    Added
                                                  </span>
                                                ) : null}
                                              </div>
                                            </div>
                                            {isActualCostSourceAdded(createActualCostRowFromRequest(row)) ? (
                                              <button
                                                type="button"
                                                onClick={() => removeActualCostSourceRows([createActualCostRowFromRequest(row)])}
                                                className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
                                              >
                                                Remove
                                              </button>
                                            ) : (
                                              <button
                                                type="button"
                                                onClick={() => appendActualCostSourceRows([createActualCostRowFromRequest(row)])}
                                                className="rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100"
                                              >
                                                Add
                                              </button>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  ))
                                ) : (
                                  <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-6 text-sm text-slate-500">
                                    No request particulars available for this J.O.
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                              <div className="mb-3">
                                <div className="text-sm font-semibold text-slate-900">Liquidation Cost Sources</div>
                                <div className="text-xs text-slate-500">
                                  Add actual cost rows from liquidation cost-incurred records already saved under this J.O.
                                </div>
                              </div>
                              <div className="space-y-3">
                                {joLiquidationSourceGroups.length ? (
                                  joLiquidationSourceGroups.map((group) => (
                                    <details
                                      key={`actual-liquidation-${group.key}`}
                                      className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                                    >
                                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3">
                                        <div className="min-w-0">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <div className="text-sm font-semibold text-slate-800">{group.liquidationNo}</div>
                                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                                              {group.rowCount} item{group.rowCount === 1 ? "" : "s"}
                                            </span>
                                          </div>
                                          <div className="mt-1 truncate text-xs text-slate-500">
                                            {group.rfpNo}
                                            {group.liquidationStatus ? ` • ${group.liquidationStatus}` : ""}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="text-right">
                                            <div className="text-xs uppercase tracking-[0.12em] text-slate-400">Total</div>
                                            <div className="text-sm font-semibold text-slate-700">{money(group.totalAmount)}</div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={(event) => {
                                              event.preventDefault();
                                              event.stopPropagation();
                                              addActualCostLiquidationGroup(group);
                                            }}
                                            className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100"
                                          >
                                            Add All
                                          </button>
                                        </div>
                                      </summary>
                                      <div className="space-y-2 border-t border-slate-100 bg-slate-50/70 p-3">
                                        {group.rows.map((row) => (
                                          <div key={`actual-liquidation-row-${row.id}`} className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 ring-1 ring-slate-100">
                                            <div className="min-w-0 flex-1">
                                              <div className="truncate text-sm font-medium text-slate-800">{row.particulars || "—"}</div>
                                              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                                                <span>O.R.: {row.or_no || "—"}</span>
                                                {isActualCostSourceAdded(createActualCostRowFromLiquidation(row)) ? (
                                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                                                    <i className="fa-solid fa-check text-[10px]"></i>
                                                    Added
                                                  </span>
                                                ) : null}
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <div className="text-sm font-semibold text-slate-700">{money(row.amount)}</div>
                                              {isActualCostSourceAdded(createActualCostRowFromLiquidation(row)) ? (
                                                <button
                                                  type="button"
                                                  onClick={() => removeActualCostSourceRows([createActualCostRowFromLiquidation(row)])}
                                                  className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700 transition-colors hover:bg-rose-100"
                                                >
                                                  Remove
                                                </button>
                                              ) : (
                                                <button
                                                  type="button"
                                                  onClick={() => appendActualCostSourceRows([createActualCostRowFromLiquidation(row)])}
                                                  className="rounded-lg border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-100"
                                                >
                                                  Add
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                  ))
                                ) : (
                                  <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-6 text-sm text-slate-500">
                                    No liquidation cost rows available for this J.O.
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : null}

                        <table className="w-full">
                          <thead>
                            <tr className="text-left text-xs uppercase tracking-[0.08em] text-slate-500">
                              <th className="pb-2 font-semibold">Source</th>
                              <th className="pb-2 font-semibold">RFP No.</th>
                              <th className="pb-2 font-semibold">Liquidation</th>
                              <th className="pb-2 font-semibold">Particulars</th>
                              <th className="pb-2 font-semibold">O.R. No.</th>
                              <th className="pb-2 text-right font-semibold">Amount</th>
                              {canEdit && <th className="pb-2"></th>}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {actualCostRows.map((row, index) => (
                              <tr key={row.id}>
                                <td className="py-2 pr-2 align-top">
                                  <span
                                    className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                      row.source_type === "liquidation_actual_cost"
                                        ? "bg-emerald-50 text-emerald-700"
                                        : row.source_type === "request_particular"
                                          ? "bg-blue-50 text-blue-700"
                                          : row.source_type === "fixed_charge"
                                            ? "bg-amber-50 text-amber-700"
                                            : "bg-slate-100 text-slate-600"
                                    }`}
                                  >
                                    {row.source_type === "liquidation_actual_cost"
                                      ? "Liquidation"
                                      : row.source_type === "request_particular"
                                        ? "Request"
                                        : row.source_type === "fixed_charge"
                                          ? "Fixed Charge"
                                          : "Manual"}
                                  </span>
                                </td>
                                <td className="py-2 pr-2 text-sm text-slate-700 align-top">
                                  {row.source_rfp_no || "—"}
                                </td>
                                <td className="py-2 pr-2 text-sm text-slate-700 align-top">
                                  {row.liquidation_no || row.liquidation_id ? (
                                    <div className="space-y-1">
                                      <div className="font-medium text-slate-800">
                                        {row.liquidation_no || row.liquidation_id}
                                      </div>
                                      {row.liquidation_status ? (
                                        <div className="text-xs text-slate-500">{row.liquidation_status}</div>
                                      ) : null}
                                    </div>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                                <td className="py-2 pr-2">
                                  <input
                                    type="text"
                                    value={row.particulars}
                                    onChange={(e) => updateActualCostRow(index, "particulars", e.target.value)}
                                    disabled={!canEdit}
                                    placeholder="Enter particular"
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-100"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="text"
                                    value={row.or_no}
                                    onChange={(e) => updateActualCostRow(index, "or_no", e.target.value)}
                                    disabled={!canEdit}
                                    placeholder="Enter O.R. No."
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm disabled:bg-slate-100"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="number"
                                    value={row.amount}
                                    onChange={(e) => updateActualCostRow(index, "amount", e.target.value)}
                                    disabled={!canEdit}
                                    placeholder="0.00"
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-right text-sm disabled:bg-slate-100"
                                  />
                                </td>
                                {canEdit && (
                                  <td className="py-2 pl-2">
                                    <button
                                      onClick={() => removeActualCostRow(index)}
                                      disabled={actualCostRows.length === 1}
                                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30"
                                    >
                                      <i className="fa-solid fa-trash-can"></i>
                                    </button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="bg-amber-50">
                              <td colSpan={5} className="py-3 px-4 text-right font-semibold text-slate-700">
                                Total Cost
                              </td>
                              <td className="py-3 px-4 text-right font-bold text-amber-700">
                                {money(actualCostTotal)}
                              </td>
                              {canEdit && <td></td>}
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>

                    {/* Gross Profit */}
                    <div className={`flex items-center justify-between rounded-2xl p-4 text-white shadow-sm ${grossProfit >= 0 ? 'bg-gradient-to-r from-emerald-600 to-emerald-700' : 'bg-gradient-to-r from-red-600 to-red-700'}`}>
                      <span className="font-semibold text-lg">
                        <i className="fa-solid fa-chart-line mr-2"></i>
                        Gross Profit (Grand Total - Actual Cost)
                      </span>
                      <span className="text-2xl font-bold">{money(grossProfit)}</span>
                    </div>

                    {/* Timeline */}
                    {actionTimeline(costAnalysis, savedReport).length > 0 && (
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <h4 className="mb-3 font-semibold text-slate-900">
                          <i className="fa-solid fa-clock-rotate-left mr-2"></i>
                          Activity Timeline
                        </h4>
                        <div className="space-y-2">
                          {actionTimeline(costAnalysis, savedReport).map((item, idx) => (
                            <div key={idx} className="flex items-center gap-3 text-sm">
                              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              <span className="font-medium text-slate-700">{item.label}</span>
                              <span className="text-slate-500">by {item.actor}</span>
                              <span className="text-slate-400">{formatDateTime(item.value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Rejection Reason */}
                    {currentStage === "rejected" && costAnalysis?.rejection_reason && (
                      <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                        <h4 className="font-semibold text-red-800 mb-2">
                          <i className="fa-solid fa-circle-xmark mr-2"></i>
                          Rejection Reason
                        </h4>
                        <p className="text-red-700">{costAnalysis.rejection_reason}</p>
                      </div>
                    )}

                    {/* Reject Input */}
                    {canReject && (
                      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                        <label className="mb-2 block text-sm font-semibold text-slate-700">
                          Rejection Reason (if rejecting)
                        </label>
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Enter reason for rejection..."
                          rows={3}
                          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-900 shadow-sm outline-none transition-all focus:border-red-500 focus:ring-2 focus:ring-red-100 resize-none"
                        />
                      </div>
                    )}

                    {/* Actions */}
                    <div className="border-t border-slate-200 pt-4">
                      <div className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                        Report actions
                      </div>
                      {canEdit && ["submitted", "partially_approved", "verified", "approved"].includes(currentStage) ? (
                        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                          <i className="fa-solid fa-circle-info mr-2" />
                          Updating this submitted cost analysis will return it to the approval process. It must be reviewed again by User 130 and User 127.
                        </div>
                      ) : null}
                      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                        {canEdit && (
                          <button
                            onClick={saveReport}
                            disabled={reportLoading}
                            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-blue-700 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-blue-200 transition-all hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[180px]"
                            style={{
                              backgroundColor: reportLoading ? "#1d4ed8" : "#1d4ed8",
                              color: "#ffffff",
                              border: "1px solid #1d4ed8",
                            }}
                          >
                            {reportLoading ? (
                              <i className="fa-solid fa-spinner fa-spin shrink-0"></i>
                            ) : (
                              <i className="fa-solid fa-pen-to-square shrink-0"></i>
                            )}
                            <span className="whitespace-nowrap">
                              {["submitted", "partially_approved", "verified", "approved"].includes(currentStage)
                                ? "Update Cost Analysis"
                                : "Save as Draft"}
                            </span>
                          </button>
                        )}

                        {!canEdit && (
                          <button
                            onClick={saveAttachedDocuments}
                            disabled={savingAttachments}
                            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-slate-700 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-slate-200 transition-all hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[200px]"
                          >
                            {savingAttachments ? (
                              <i className="fa-solid fa-spinner fa-spin shrink-0"></i>
                            ) : (
                              <i className="fa-solid fa-paperclip shrink-0"></i>
                            )}
                            <span className="whitespace-nowrap">Update Attached Documents</span>
                          </button>
                        )}

                        {canSubmit && (
                          <button
                            onClick={submitForApproval}
                            disabled={actionLoading === "submit"}
                            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-emerald-700 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition-all hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[220px]"
                          >
                            {actionLoading === "submit" ? (
                              <i className="fa-solid fa-spinner fa-spin shrink-0"></i>
                            ) : (
                              <i className="fa-solid fa-paper-plane shrink-0"></i>
                            )}
                            <span className="whitespace-nowrap">Submit for Approval</span>
                          </button>
                        )}

                        {canVerify && (
                          <button
                            onClick={verifyAnalysis}
                            disabled={actionLoading === "approve_initial"}
                            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-violet-700 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-violet-200 transition-all hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[160px]"
                          >
                            {actionLoading === "approve_initial" ? (
                              <i className="fa-solid fa-spinner fa-spin shrink-0"></i>
                            ) : (
                              <i className="fa-solid fa-clipboard-check shrink-0"></i>
                            )}
                            <span className="whitespace-nowrap">Approve for Final Review</span>
                          </button>
                        )}

                        {canApprove && (
                          <button
                            onClick={approveAnalysis}
                            disabled={actionLoading === "approve"}
                            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-emerald-700 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-200 transition-all hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[160px]"
                          >
                            {actionLoading === "approve" ? (
                              <i className="fa-solid fa-spinner fa-spin shrink-0"></i>
                            ) : (
                              <i className="fa-solid fa-circle-check shrink-0"></i>
                            )}
                            <span className="whitespace-nowrap">
                              {currentStage === "submitted" ? "Approve Directly" : "Approve"}
                            </span>
                          </button>
                        )}

                        {canReject && (
                          <button
                            onClick={rejectAnalysis}
                            disabled={actionLoading === "reject" || !rejectReason.trim()}
                            className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-red-700 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-red-200 transition-all hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[160px]"
                          >
                            {actionLoading === "reject" ? (
                              <i className="fa-solid fa-spinner fa-spin shrink-0"></i>
                            ) : (
                              <i className="fa-solid fa-circle-xmark shrink-0"></i>
                            )}
                            <span className="whitespace-nowrap">Reject</span>
                          </button>
                        )}

                        {verifyDisabledReason && (
                          <p className="mt-2 w-full text-sm text-amber-700">
                            <i className="fa-solid fa-info-circle mr-1"></i>
                            {verifyDisabledReason}
                          </p>
                        )}

                        <button
                          onClick={closeModal}
                          className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 sm:w-auto sm:min-w-[160px]"
                        >
                          <i className="fa-solid fa-xmark shrink-0"></i>
                          <span className="whitespace-nowrap">Close modal</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {modalMode === "preview" ? (
                  <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
                    {previewDocType === "cost" ? (
                      <button
                        type="button"
                        onClick={printCostAnalysisPreview}
                        className="rounded-2xl bg-blue-700 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-800"
                        style={{
                          backgroundColor: "#1d4ed8",
                          border: "1px solid #1d4ed8",
                          color: "#ffffff",
                        }}
                      >
                        <i className="fa-solid fa-print mr-2" />
                        Print Cost Analysis
                      </button>
                    ) : null}
                    {canEdit ? (
                      <button
                        type="button"
                        onClick={() => setModalMode("editor")}
                        className="rounded-2xl border border-blue-200 bg-blue-50 px-5 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
                      >
                        Open Editor
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={closeModal}
                      className="rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                    >
                      Close
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Attachment Modal */}
      {attachmentModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Supporting Documents</h3>
                  <p className="text-sm text-slate-500">
                    {attachmentModalData?.job_order || attachmentModalData?.rfp_no || "Cost Analysis"}
                  </p>
                </div>
                <button
                  onClick={closeAttachmentModal}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <i className="fa-solid fa-times text-slate-500"></i>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-4">
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx"
                    onChange={handleAttachmentUpload}
                    className="hidden"
                    id="attachment-upload"
                  />
                  <label
                    htmlFor="attachment-upload"
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-dashed border-violet-300 bg-violet-50 text-violet-700 font-medium cursor-pointer hover:bg-violet-100 transition-colors ${
                      uploadingAttachments ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    {uploadingAttachments ? (
                      <i className="fa-solid fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fa-solid fa-cloud-upload-alt"></i>
                    )}
                    {uploadingAttachments ? "Uploading..." : "Upload Files"}
                  </label>
                  <p className="text-xs text-slate-500 mt-2">
                    Supported: PDF, Images, Word, Excel (Max 15MB each)
                  </p>
                </div>

                {attachmentsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <i className="fa-solid fa-spinner fa-spin text-2xl text-slate-400"></i>
                  </div>
                ) : attachments.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <i className="fa-solid fa-folder-open text-4xl mb-2 opacity-30"></i>
                    <p>No attachments uploaded yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {attachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="flex items-center justify-between p-3 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                            {attachment.file_type?.includes("pdf") ? (
                              <i className="fa-solid fa-file-pdf text-red-500"></i>
                            ) : attachment.file_type?.includes("image") ? (
                              <i className="fa-solid fa-file-image text-blue-500"></i>
                            ) : attachment.file_type?.includes("word") || attachment.file_type?.includes("document") ? (
                              <i className="fa-solid fa-file-word text-blue-600"></i>
                            ) : attachment.file_type?.includes("sheet") || attachment.file_type?.includes("excel") ? (
                              <i className="fa-solid fa-file-excel text-green-600"></i>
                            ) : (
                              <i className="fa-solid fa-file text-slate-500"></i>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {attachment.file_name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {attachment.uploaded_by_name || "Unknown"} •{" "}
                              {new Date(attachment.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <a
                            href={attachment.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                            title="View"
                          >
                            <i className="fa-solid fa-external-link-alt"></i>
                          </a>
                          <button
                            onClick={() => handleDeleteAttachment(attachment.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                            title="Delete"
                          >
                            <i className="fa-solid fa-trash"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200 px-6 py-4">
                <button
                  onClick={closeAttachmentModal}
                  className="w-full px-4 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {templateModalOpen &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 p-4">
            <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-slate-200 bg-slate-900 px-6 py-5 text-white">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-300">Cost Analysis</p>
                  <h2 className="mt-1 text-2xl font-bold">Create Cost Analysis Template</h2>
                </div>
                <button type="button" onClick={() => setTemplateModalOpen(false)} className="rounded-full p-2 text-slate-300 hover:bg-white/10 hover:text-white" aria-label="Close template modal">
                  <i className="fa-solid fa-xmark text-xl" />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-6">
                {templateLoading ? (
                  <div className="py-12 text-center text-slate-500"><i className="fa-solid fa-spinner fa-spin mr-2" />Loading clients and templates...</div>
                ) : (
                  <>
                    <label className="mb-2 block text-sm font-semibold text-slate-700">Corporate Client</label>
                    <select value={templateClient} onChange={(event) => loadSelectedTemplate(event.target.value)} className="mb-6 w-full rounded-xl border border-slate-200 px-4 py-3 text-slate-900 outline-none focus:border-blue-500 sm:max-w-xl">
                      <option value="">Select corporate client...</option>
                      {templateClients.map((client) => <option key={client} value={client}>{client}</option>)}
                    </select>
                    <div className="overflow-x-auto rounded-2xl border border-slate-200">
                      <div className="flex items-center justify-between bg-slate-900 px-4 py-3 text-white">
                        <span className="font-semibold">Default Billing Charges</span>
                        <button type="button" onClick={() => setTemplateBillingRows((rows) => [...rows, createBillingChargeRow()])} className="rounded-lg bg-white/10 px-3 py-1.5 text-sm hover:bg-white/20"><i className="fa-solid fa-plus mr-1" />Add Row</button>
                      </div>
                      <table className="w-full min-w-[760px]">
                        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3">Particulars</th><th className="px-4 py-3 text-center">VAT</th><th className="px-4 py-3 text-center">EWT</th><th className="px-4 py-3">EWT Rate</th><th className="px-4 py-3 text-right">Base Amount</th><th /></tr></thead>
                        <tbody className="divide-y divide-slate-100">
                          {templateBillingRows.map((row, index) => (
                            <tr key={row.id}>
                              <td className="px-4 py-3"><select value={row.particulars || ""} onChange={(e) => setTemplateBillingRows((rows) => rows.map((item, i) => i === index ? { ...item, particulars: e.target.value } : item))} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"><option value="">Select particular...</option>{particularsOptions.map((particular) => <option key={particular.id} value={particular.name}>{particular.name}</option>)}</select></td>
                              <td className="px-4 py-3 text-center"><input type="checkbox" checked={Boolean(row.has_output_vat)} onChange={(e) => setTemplateBillingRows((rows) => rows.map((item, i) => i === index ? { ...item, has_output_vat: e.target.checked } : item))} className="h-5 w-5" /></td>
                              <td className="px-4 py-3 text-center"><input type="checkbox" checked={Boolean(row.has_ewt)} onChange={(e) => setTemplateBillingRows((rows) => rows.map((item, i) => i === index ? { ...item, has_ewt: e.target.checked } : item))} className="h-5 w-5" /></td>
                              <td className="px-4 py-3"><input value={row.ewt_rate || ""} onChange={(e) => setTemplateBillingRows((rows) => rows.map((item, i) => i === index ? { ...item, ewt_rate: e.target.value } : item))} placeholder="%" className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm" /></td>
                              <td className="px-4 py-3"><input type="number" value={row.base_amount ?? ""} onChange={(e) => setTemplateBillingRows((rows) => rows.map((item, i) => i === index ? { ...item, base_amount: e.target.value } : item))} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-right text-sm" /></td>
                              <td className="px-4 py-3 text-right"><button type="button" onClick={() => setTemplateBillingRows((rows) => rows.length > 1 ? rows.filter((_, i) => i !== index) : [createBillingChargeRow()])} className="text-rose-600 hover:text-rose-800" aria-label="Remove row"><i className="fa-solid fa-trash" /></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
                <button type="button" onClick={() => setTemplateModalOpen(false)} className="rounded-xl border border-slate-200 px-5 py-3 font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                <button type="button" onClick={saveTemplate} disabled={templateLoading || templateSaving || !templateClient} className="rounded-xl bg-emerald-700 px-5 py-3 font-semibold text-white hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50">{templateSaving ? "Saving..." : "Save Template"}</button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Notification Modal (Error/Warning) */}
      {notificationModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
              <div
                className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  notificationModal.type === "error"
                    ? "bg-red-100"
                    : notificationModal.type === "warning"
                    ? "bg-amber-100"
                    : "bg-blue-100"
                }`}
              >
                <i
                  className={`text-3xl ${
                    notificationModal.type === "error"
                      ? "fa-solid fa-circle-exclamation text-red-600"
                      : notificationModal.type === "warning"
                      ? "fa-solid fa-triangle-exclamation text-amber-600"
                      : "fa-solid fa-info-circle text-blue-600"
                  }`}
                ></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{notificationModal.title}</h3>
              <p className="text-slate-600 mb-6">{notificationModal.message}</p>
              <button
                onClick={() => setNotificationModal(null)}
                className={`px-6 py-3 font-semibold rounded-xl transition-colors ${
                  notificationModal.type === "error"
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : notificationModal.type === "warning"
                    ? "bg-amber-600 hover:bg-amber-700 text-white"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                OK
              </button>
            </div>
          </div>,
          document.body
        )}

      {/* Success Modal */}
      {successModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <i className="fa-solid fa-check text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{successModal.title}</h3>
              <p className="text-slate-600 mb-6">{successModal.message}</p>
              <button
                onClick={() => setSuccessModal(null)}
                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors"
              >
                OK
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CostAnalysisWorkflow;
